import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { AuthType } from 'constants/constants'

import {
  Box,
  Typography,
  Modal,
  Button,
  Grid,
} from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { MasterSearchFrom, SearchBox } from 'components/commonparts'
import { ItemInfoDisplay } from 'components/dialog'
import { SearchCondition, ItemRenewalSimulator } from 'components/parts/StockCenter'
import DataList from 'components/parts/StockCenter/StockGrid/DataList'
import { addDays, isSameDay, differenceInCalendarDays } from 'date-fns'
import {
  useCalcStock,
  useCalcStockParams,
  handleStockCalculation,
  buildCalcStockParamsForCenter,
  getStockCenter2ShipRawValue,
  getStockCenter2EffectiveArrivalQuantity,
} from 'hooks/useCalcStock'
import { useCenter } from 'hooks/useCenter'
import { useCenterSupplier } from 'hooks/useCenterSupplier'
import { useGridSearch } from 'hooks/useGridSearch'
import { useOrderAlert } from 'hooks/useOrderAlert'
import { useOrderQuantityOptimize, type StockList as OrderQuantityStockList } from 'hooks/useOrderQuantityOptimize'
import { useRenewalSimulator } from 'hooks/useRenewalSimulator'
import { useStock, type StockExtended } from 'hooks/useStock'
import { useSupplier } from 'hooks/useSupplier'
import _ from 'lodash'
import { useSearchParamStore } from 'store/searchParamStore'
import { DialogWrapper } from 'templates'
import {
  ROW_TYPE_ARRIVAL_QUANTITY,
  ROW_TYPE_ORDER_QUANTITY_REC,
  ROW_TYPE_ORDER_QUANTITY_RECOMMEND,
  ROW_TYPE_ARRIVAL_QUANTITY_PLAN,
  ROW_TYPE_ORDER_QUANTITY,
  ROW_TYPE_SHIP_QUANTITY,
  ROW_TYPE_SHIP_QUANTITY_EXPECTED,
  ROW_TYPE_SALESORDER_QUANTITY,
  ROW_TYPE_SALESORDER_PLAN,
  ROW_TYPE_STOCK_DAYS,
  ROW_TYPE_AVG_SHIP_QUANTITY_STOCK_BASE,
  ROW_TYPE_STOCK_QUANTITY,
  ROW_TYPE_STOCK_QUANTITY_REC,
  ROW_TYPE_CALC_SAFETY,
  ROW_TYPE_STOCK_QUANTITY_SHIP_STOP,
  ROW_TYPE_REMARKS,
} from 'types/table-model2'
import { normalizeDate, convertDateToRequestParam, isEqualDate, valueToDateStr } from 'utils/convertFromLocalDate'
import { getInitCompanyId } from 'utils/getInitParam'
import { getLocalSearchParam } from 'utils/getLocalSearchParam'
import { getDisplayDaylistFromCenterSupplier } from 'utils/stockDaylist'

import type { CellValueChangedEvent, IRowNode, ModelUpdatedEvent } from 'ag-grid-enterprise'
import type { GetMstCentersCenterGetRequest, GetStockStockGetRequest, MstSupplier, MstCenterSupplier, ResponseResult, Stock, Daylist, StockPlanUpdateBase, DaylistCenterSupplier, MstCenter } from 'api-client'
import type { CalcStockParams } from 'hooks/useCalcStock'
import type { StockRow, StockCell } from 'types/table-model2'

type StockCenterSearchParam = GetStockStockGetRequest & { orgItemCodes?: string[] }

function toValidNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

/** Popup remarks editor may pass a raw string; numeric editors / valueParser pass StockCell-shaped objects. */
function extractNewValueScalarFromCellEvent(newValue: unknown): string | number | null | undefined {
  if (newValue == null) {
    return undefined
  }
  if (typeof newValue === 'string' || typeof newValue === 'number') {
    return newValue
  }
  if (typeof newValue === 'object' && 'value' in newValue) {
    return (newValue as StockCell).value as string | number | null | undefined
  }
  return undefined
}

function extractRecDateFromCellChange(newValue: unknown, oldValue: StockCell | undefined) {
  if (newValue != null && typeof newValue === 'object' && 'recDate' in newValue) {
    return (newValue as StockCell).recDate
  }
  return oldValue?.recDate ?? null
}

function calculateStockCenter2ShipDisplayValue(
  options: {
    shipQuantityPlan?: number | null | unknown
    salesorderQuantity?: number | null
    recDate?: Date | string | null
    shipQuantityScheduled?: number | null
  },
  calcParams: CalcStockParams
): number {
  const shipQuantityRaw = getStockCenter2ShipRawValue(
    {
      shipQuantityPlan: options.shipQuantityPlan,
      shipQuantityScheduled: options.shipQuantityScheduled,
    },
    options.recDate != null
      ? {
          recDate: options.recDate,
          systemDate: calcParams.systemDate,
          todayShipRecDelayFlg: calcParams.todayShipRecDelayFlg,
        }
      : null
  )
  const recDate = normalizeDate(options.recDate)

  if (!recDate) {
    return shipQuantityRaw
  }

  if (
    isSameDay(recDate, calcParams.systemDate) ||
    (recDate > calcParams.systemDate && calcParams.calcStockShippingorderFlg)
  ) {
    return shipQuantityRaw + Number(options.salesorderQuantity ?? 0)
  }

  return shipQuantityRaw
}

function isStockCenter2PastRecDate(
  recDate: Date | string | null | undefined,
  systemDate: Date
): boolean {
  const rec = normalizeDate(recDate)
  const sys = normalizeDate(systemDate)
  return !!(rec && sys && rec < sys)
}

function getStockCenter2InitialShipDisplayValue(
  options: {
    shipQuantityStockBase?: number | null
    shipQuantityPlan?: number | null | unknown
    shipQuantityScheduled?: number | null
    salesorderQuantity?: number | null
    recDate?: Date | string | null
  },
  calcParams: CalcStockParams
): number {
  if (isStockCenter2PastRecDate(options.recDate, calcParams.systemDate)) {
    return Number(options.shipQuantityStockBase ?? 0)
  }

  return calculateStockCenter2ShipDisplayValue(
    {
      shipQuantityPlan: options.shipQuantityPlan,
      shipQuantityScheduled: options.shipQuantityScheduled,
      salesorderQuantity: options.salesorderQuantity,
      recDate: options.recDate,
    },
    calcParams
  )
}

function isStockCenter2PlanOverride(
  planValue: number | null,
  baseValue: number | null | undefined
): boolean {
  if (planValue == null) {
    return false
  }

  return Number(planValue) !== Number(baseValue ?? 0)
}

// Helper function to show result message
function showResultMessage(
  setResultMessage: (msg: ResponseResult | undefined) => void,
  options: { status: 'success' | 'warning' | 'error'; message: string }
) {
  setResultMessage({
    status: options.status,
    message: options.message,
    pageNo: 0,
    systemDate: normalizeDate(Date.now()),
    systemInfo: {
      systemDate: null,
      sysCurrentStatus: [],
      sysProcessingCount: 0,
      companyStatus: '',
      systemStatus: ''
    }
  });
}

/**
 * subtotal の groupKey 形式: `subtotal__<supplierName|itemName|centerName>__<key>`.
 * 各 field に対応する key 抽出規則は DataList.tsx の getTopGroupMeta と同一。
 */
const SUBTOTAL_KEY_PATTERN = /^subtotal__(supplierName|itemName|centerName)__(.+)$/

type SubtotalField = 'supplierName' | 'itemName' | 'centerName'

function getSubtotalKeyForField(row: StockRow, field: SubtotalField): string {
  if (field === 'supplierName') {
    return row.orgSupplierId != null && row.orgSupplierId !== ''
      ? String(row.orgSupplierId)
      : 'unknown_supplier'
  }
  if (field === 'itemName') {
    return row.itemCode != null && row.itemCode !== '' ? String(row.itemCode) : 'unknown_item'
  }
  return row.centerId != null ? String(row.centerId) : 'unknown_center'
}

/**
 * 合計行（subtotal / grand total）の groupKey に基づき、scope 内 leaf 行を抽出する。
 * - groupKey === '合計' → 全 leaf
 * - subtotal__field__key → field の key が一致する leaf
 * - その他 → 全 leaf（fallback）
 */
function filterLeafRowsByTotalScope(
  totalNode: IRowNode<StockRow>,
  leafRows: IRowNode<StockRow>[]
): IRowNode<StockRow>[] {
  const groupKey = totalNode.data?.groupKey ?? ''
  if (groupKey === '合計') return leafRows

  const matched = SUBTOTAL_KEY_PATTERN.exec(groupKey)
  if (!matched) return leafRows

  const field = matched[1] as SubtotalField
  const subtotalKey = matched[2]

  return leafRows.filter((n) => {
    const row = n.data
    return row != null && getSubtotalKeyForField(row, field) === subtotalKey
  })
}

function StockCenter2() {
  const { loginUserInfo, typeCode: typeCodes } = useAuthContext()
  const defaultOptimizeRange = typeCodes?.find((type) => type.typeGroup === 'optimize_range')?.codes.find((type) => type.defaultFlg)?.typeCode
  const SearchDateRangeStartShortGroup = typeCodes?.find((type) => type.typeGroup === 'search_date_range_start_short')
  const SearchDateRangeEndGroup = typeCodes?.find((type) => type.typeGroup === 'search_date_range_end')
  const StartDateTypeCodeList = SearchDateRangeStartShortGroup?.codes.map((type) => type.typeCode)
  const EndDateTypeCodeList = SearchDateRangeEndGroup?.codes.map((type) => type.typeCode)
  const defaultStartCode = SearchDateRangeStartShortGroup?.codes.find((type) => type.defaultFlg)?.typeCode
  const defaultEndCode = SearchDateRangeEndGroup?.codes.find((type) => type.defaultFlg)?.typeCode

  const setSearchParamState = useSearchParamStore((state) => state.setSearchParamState)
  const searchParamState = useSearchParamStore((state) => state.searchParamState)

  const { search } = useLocation()
  const query = new URLSearchParams(search)

  const hasCenterIdsQuery = query.has('center_ids')
  const hasOrgSupplierIdsQuery = query.has('org_supplier_ids')
  const hasItemCodesQuery = query.has('item_codes')

  // Query parameters - centerIds, orgSupplierIds, itemCodes (array)
  const trQueryParam = {
    companyId: Number(query.get('company_id')),
    centerIds: query.get('center_ids')?.split(',').map(Number).filter(Boolean) || [],
    orgSupplierIds: query.get('org_supplier_ids')?.split(',').filter(Boolean) || [],
    itemCodes: query.get('item_codes')?.split(',').filter(Boolean) || [],
    viewUnitType: query.get('view_unit_type'),
    startDateTypeCode: query.get('start_date'),
    endDateTypeCode: query.get('end_date'),
  }

  // Get search conditions from local storage
  const localSearchParam = getLocalSearchParam(searchParamState, loginUserInfo?.tenantId, loginUserInfo?.companyId)

  const localCompanyId = localSearchParam?.stockCenter.companyId || undefined
  const localCenterIds = localSearchParam?.stockCenter.centerIds || []
  const localViewUnitType = localSearchParam?.stockCenter.viewUnitType || 'case'
  const localOrgSupplierIds = localSearchParam?.stockCenter.orgSupplierIds || []
  const localItemCodes = localSearchParam?.stockCenter.itemCodes || []
  const localOrgItemCodes = (localSearchParam?.stockCenter as any)?.orgItemCodes || []
  const localStartDate = localSearchParam?.stockCenter.startDate || ''
  const localEndDate = localSearchParam?.stockCenter.endDate || ''
  const localStartDateTypeCode = localSearchParam?.stockCenter.startDateTypeCode || ''
  const localEndDateTypeCode = localSearchParam?.stockCenter.endDateTypeCode || ''

  /** ローカルに center / supplier / item のいずれも未保存のとき、初期化・CSV/Excel を無効化 */
  const disableToolbarActionsWithoutLocalSearch = useMemo(
    () =>
      localCenterIds.length === 0 &&
      localOrgSupplierIds.length === 0 &&
      localItemCodes.length === 0,
    [localCenterIds, localOrgSupplierIds, localItemCodes]
  )

  // Query precedence per field:
  // if URL key exists, use URL value as-is (including empty = explicit clear);
  // if URL key does not exist, fallback to local storage.
  const effectiveCenterIds = hasCenterIdsQuery ? trQueryParam.centerIds : localCenterIds
  const effectiveOrgSupplierIds = hasOrgSupplierIdsQuery ? trQueryParam.orgSupplierIds : localOrgSupplierIds
  const effectiveItemCodes = hasItemCodesQuery ? trQueryParam.itemCodes : localItemCodes
  const effectiveOrgItemCodes = hasItemCodesQuery ? [] : localOrgItemCodes

  const initSearchParam: StockCenterSearchParam = {
    companyId: getInitCompanyId(trQueryParam.companyId, localCompanyId, loginUserInfo ? loginUserInfo.companyId : 0),
    centerIds: effectiveCenterIds.length > 0 ? effectiveCenterIds : undefined,
    viewUnitType: trQueryParam.viewUnitType ? trQueryParam.viewUnitType : localViewUnitType,
    orgSupplierIds: effectiveOrgSupplierIds.length > 0 ? effectiveOrgSupplierIds : undefined,
    itemCodes: effectiveItemCodes.length > 0 ? effectiveItemCodes : undefined,
    orgItemCodes: effectiveOrgItemCodes.length > 0 ? effectiveOrgItemCodes : undefined,
    orderPlanFlg: false,
    startDate: normalizeDate(localStartDate),
    endDate: normalizeDate(localEndDate),
    startDateTypeCode: trQueryParam.startDateTypeCode && StartDateTypeCodeList?.includes(trQueryParam.startDateTypeCode) ? trQueryParam.startDateTypeCode : localStartDateTypeCode || defaultStartCode,
    endDateTypeCode: trQueryParam.endDateTypeCode && EndDateTypeCodeList?.includes(trQueryParam.endDateTypeCode) ? trQueryParam.endDateTypeCode : localEndDateTypeCode || defaultEndCode,
  }

  // Data fetching hooks
  const {
    result,
    stockData,
    fetchStockData,
    fetchUpdateStock,
    isLoading,
    setIsLoading: setStockIsLoading,
    isUpdating,
    fetchStockUploadFile,
    fetchStockCsvDownload,
    isCsvLoading: csvDownloadLoading,
    fetchStockXlsxDownload,
    isXlsxLoading: xlsxDownloadLoading,
    fetchUpdateStockCenter,
  } = useStock()

  const { fetchCenterSearch, centerSearch } = useCenter()
  // Center Supplier hook for order optimization
  const { centerSupplierSearchResult, centerSupplierSearch, fetchCenterSupplierSearch, centerSupplierSearchIsLoading } = useCenterSupplier()
  const { supplierSearch, fetchSupplierSearch } = useSupplier()
  const { fetchAlertSummary } = useOrderAlert()

  // Order quantity optimization hook
  const { optimizeOrderQuantity, isLoading: isOptimizingOrderQuantity, result: optimizeOrderQuantityResult } = useOrderQuantityOptimize()

  const supplierMap = useMemo(() => {
    const map = new Map<string, string>()
    supplierSearch?.list?.forEach((supplier) => {
      if (supplier.orgSupplierId != null && supplier.supplierName != null) {
        map.set(supplier.orgSupplierId, supplier.supplierName)
      }
    })
    return map
  }, [supplierSearch?.list])

  // State management
  const [searchParam, setSearchParam] = useState<StockCenterSearchParam>(initSearchParam)
  const [supplierInfo, setSupplierInfo] = useState<MstSupplier | undefined>(undefined)
  const [trCompanyId, setTrCompanyId] = useState<number>(
    getInitCompanyId(trQueryParam.companyId, localCompanyId, loginUserInfo ? loginUserInfo.companyId : 0)
  )
  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)
  const [uploadResultMessage, setUploadResultMessage] = useState<ResponseResult | undefined>(undefined)
  const [searchTrigger, setSearchTrigger] = useState<boolean>(false)
  const [gridMessage, setGridMessage] = useState<ResponseResult | undefined>(undefined)
  const [discardChangeDialogOpen, setDiscardChangeDialogOpen] = useState<boolean>(false)
  const [hasGridEdited, setHasGridEdited] = useState<boolean>(false)
  const [rowData, setRowData] = useState<StockRow[]>([])
  const [gridInstanceKey, setGridInstanceKey] = useState(0)
  /** DataList 内の列メニュー状態を検索・初期化のたびにリセットするためのカウンタ */
  const [gridMenuResetSeq, setGridMenuResetSeq] = useState(0)
  const [downloadMode, setDownloadMode] = useState<string>('csv')
  const [downloadColDef, setDownloadColDef] = useState<any[] | undefined>(undefined)
  const [loadingState, setLoadingState] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [mismatchedStockItemCodes, setMismatchedStockItemCodes] = useState<string[]>([])
  const [itemInfoDialog, setItemInfoDialog] = useState<{ open: boolean; centerId?: number; itemCode?: string }>({
    open: false,
    centerId: undefined,
    itemCode: undefined,
  })

  // Grid search functionality
  const gridSearch = useGridSearch({ autoShowOnCtrlF: true })
  /** While clearing isCurrentUpdate after register, ignore onCellValueChanged (setDataValue would re-enter). */
  const suppressRegisterCellChangeRef = useRef(false)

  const renewalSimulator = useRenewalSimulator()

  // Stock calculation hooks
  const calcStockHook = useCalcStock()
  const { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays: calculateStockDaysHook } = calcStockHook
  const gridCalc = useMemo(() => calcStockHook.getGridCalculators(), [calcStockHook])
  const childrenCenterId = useMemo(() => stockData?.list?.[0]?.centerId, [stockData?.list])
  const calcParams = useCalcStockParams(
    loginUserInfo?.systemDate,
    childrenCenterId ?? searchParam.centerId,
    searchParam.companyId,
    centerSearch
  )

  // Check if table is editable
  const tableEditable = useMemo(
    (): boolean =>
      !!loginUserInfo && loginUserInfo.companyId === trCompanyId && loginUserInfo?.authType !== AuthType.ReadOnly,
    [trCompanyId, loginUserInfo]
  )

  // Grid unit type
  const gridUnitType = useMemo(
    (): 'case' | 'pallet' => (searchParam.viewUnitType === 'pallet' ? 'pallet' : 'case'),
    [searchParam.viewUnitType]
  )

  /** Display day columns (unique by dateno) from daylist_center_supplier to avoid duplicates when multiple centers/suppliers are selected. */
  const displayDaylist = useMemo(
    () => getDisplayDaylistFromCenterSupplier(stockData?.daylistCenterSupplier),
    [stockData?.daylistCenterSupplier]
  )

  const navigate = useNavigate()

  const resultToRows = useCallback((dataList: Array<StockExtended>, dayList: Array<Daylist>): StockRow[] => {
    if (!dataList || dataList.length === 0) return []

    const groupKeyOf = (s: StockExtended) => `${s.centerId ?? 0}_${s.orgSupplierId ?? ''}_${s.itemCode ?? ''}`
    const dataByGroup = _.groupBy(dataList, groupKeyOf)
    const rows: StockRow[] = []

    /*
     * データ行作成 (Create data rows for each product)
     */
    const sortedGroupKeys = Object.keys(dataByGroup).sort((keyA, keyB) => {
      const stocksA = dataByGroup[keyA]
      const stocksB = dataByGroup[keyB]
      const a = stocksA?.[0]
      const b = stocksB?.[0]
      if (!a || !b) return 0
      if ((a.centerId ?? 0) !== (b.centerId ?? 0)) return (a.centerId ?? 0) - (b.centerId ?? 0)
      const suppA = a.orgSupplierId ?? ''
      const suppB = b.orgSupplierId ?? ''
      if (suppA !== suppB) return suppA.localeCompare(suppB)
      return (a.orgItemCode ?? '').localeCompare(b.orgItemCode ?? '')
    })

    sortedGroupKeys.forEach((groupKey) => {
      const stocks = dataByGroup[groupKey]
      if (!stocks || stocks.length === 0) return
      const today = stocks.find((stock: StockExtended) => stock.rectype === 'today')
      const {
        companyId,
        centerId,
        centerName,
        orgSupplierId,
        orgItemCode,
        itemCode,
        itemName,
        janCase,
        expectedRank,
        predictionMapeMedian,
        orderUnitTypeName,
        caseLot,
        minOrderQuantity,
        stockDaysSetting,
        safetyStockSetting,
        minStockQuantity,
        startDate,
        stopShip,
        stopArrival,
        renewalFlg,
        renewalNumber,
        weight,
        maxPalletQuantity,
        optimizeType,
        isAlertMaster,
        isMinShipDateAlert,
        isEndSaleAlert,
        isInactive,
        isExpectedRankAlert,
        shipdaysNextArrival

      } = today || stocks[0] as any

      if (!companyId || !centerId || !itemCode || !orgItemCode) {
        return
      }

      const supplierName = orgSupplierId ? supplierMap.get(orgSupplierId) ?? null : null

      const parentData = {
        companyId,
        centerId,
        centerName,
        orgSupplierId: orgSupplierId ?? null,
        supplierName,
        orgItemCode,
        itemCode,
        itemName: itemName ?? null,
        janCase: janCase ?? null,
        expectedRank,
        predictionMapeMedian: predictionMapeMedian ?? null,
        orderUnitTypeName: orderUnitTypeName ?? null,
        caseLot: caseLot ?? null,
        minOrderQuantity: minOrderQuantity ?? null,
        stockDaysSetting,
        safetyStockSetting,
        minStockQuantity,
        startDate,
        stopShip,
        stopArrival,
        renewalFlg,
        renewalNumber,
        weight,
        maxPalletQuantity,
        optimizeType,
        isTotal: false,
        groupKey,
        isAlertMaster,
        isMinShipDateAlert,
        isEndSaleAlert,
        isInactive,
        isExpectedRankAlert,
        shipdaysNextArrival
      }

      const rowCalcParams = buildCalcStockParamsForCenter(
        loginUserInfo?.systemDate,
        centerId,
        companyId,
        centerSearch
      )

      const arrivalRow: StockRow = { ...parentData, rowType: ROW_TYPE_ARRIVAL_QUANTITY }
      const orderQuantityRecRow: StockRow = { ...parentData, rowType: ROW_TYPE_ORDER_QUANTITY_REC }
      const orderQuantityRecommendRow: StockRow = { ...parentData, rowType: ROW_TYPE_ORDER_QUANTITY_RECOMMEND }
      const arrivalQuantityPlanRow: StockRow = { ...parentData, rowType: ROW_TYPE_ARRIVAL_QUANTITY_PLAN }
      const orderQuantityRow: StockRow = { ...parentData, rowType: ROW_TYPE_ORDER_QUANTITY }
      const shipRow: StockRow = { ...parentData, rowType: ROW_TYPE_SHIP_QUANTITY }
      const shipQuantityExpectedRow: StockRow = { ...parentData, rowType: ROW_TYPE_SHIP_QUANTITY_EXPECTED }
      const salesOrderRow: StockRow = { ...parentData, rowType: ROW_TYPE_SALESORDER_QUANTITY }
      const shipQuantityPlanRow: StockRow = { ...parentData, rowType: ROW_TYPE_SALESORDER_PLAN }
      const stockQuantityRow: StockRow = { ...parentData, rowType: ROW_TYPE_STOCK_QUANTITY }
      const stockQuantityRecRow: StockRow = { ...parentData, rowType: ROW_TYPE_STOCK_QUANTITY_REC }
      const calcSafetyRow: StockRow = { ...parentData, rowType: ROW_TYPE_CALC_SAFETY }
      const stockQuantityShipStopRow: StockRow = { ...parentData, rowType: ROW_TYPE_STOCK_QUANTITY_SHIP_STOP }
      const stockDaysRow: StockRow = { ...parentData, rowType: ROW_TYPE_STOCK_DAYS }
      const avgShipQuantityStockBaseRow: StockRow = { ...parentData, rowType: ROW_TYPE_AVG_SHIP_QUANTITY_STOCK_BASE }
      const remarksRow: StockRow = { ...parentData, rowType: ROW_TYPE_REMARKS }

      dayList.forEach((day) => {
        if (!day.datevalue || !day.dateno) return

        const matchedData = stocks.find(
          (stock) => stock.recDate && day.datevalue && isSameDay(stock.recDate, day.datevalue)
        )

        if (matchedData && matchedData.recDate) {
          const commonColumn = {
            recDate: matchedData.recDate,
            orderDate: matchedData.orderDate,
            isCurrentUpdate: false,
            oldValue: null,
            enableData: false,
            updateDatetime: matchedData.updateDatetime,
            stockPlanUpdateDatetime: matchedData.stockPlanUpdateDatetime,
          }
          arrivalRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: getStockCenter2EffectiveArrivalQuantity({
              enableUpdateArrival: matchedData.enableUpdateArrival,
              arrivalQuantity: matchedData.arrivalQuantity,
              orderQuantity: matchedData.orderQuantity,
              arrivalQuantityPlan: matchedData.arrivalQuantityPlan,
              orderQuantityRec: matchedData.orderQuantityRec,
              orderQuantityRecommend: matchedData.orderQuantityRecommend,
            }),
            enableUpdate: matchedData.enableUpdateArrival || false,
            isUpdate: matchedData.isUpdatedArrival || false,
            recType: day.rectype,
            fixFlg: matchedData.arrivalFixFlg || false,
            orderQuantityRec: matchedData.orderQuantityRec || null,
            isOrderConfirmed: matchedData.isOrderConfirmed || false,
            isUpdatedArrivalTmp: matchedData.isUpdatedArrivalTmp || false,
            isOrderDay: matchedData.isOrderDay || false,
            isArrivalDay: matchedData.isArrivalDay || false,
            isTodayOrderDay: today?.isOrderDay || false,
            isAlertMaster: today?.isAlertMaster || false,
            isMinShipDateAlert: today?.isMinShipDateAlert || false,
            isEndSaleAlert: today?.isEndSaleAlert || false,
            isInactive: today?.isInactive || false,
            isExpectedRankAlert: today?.isExpectedRankAlert || false,
          }

          shipRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: getStockCenter2InitialShipDisplayValue({
              shipQuantityStockBase: matchedData.shipQuantityStockBase,
              shipQuantityPlan: matchedData.shipQuantityPlan,
              shipQuantityScheduled: matchedData.shipQuantityScheduled,
              salesorderQuantity: matchedData.salesorderQuantity,
              recDate: matchedData.recDate,
            }, rowCalcParams),
            shipQuantityRaw: isStockCenter2PastRecDate(matchedData.recDate, rowCalcParams.systemDate)
                ? (matchedData.shipQuantityStockBase ?? 0)
                : getStockCenter2ShipRawValue(
                  {
                    shipQuantityPlan: matchedData.shipQuantityPlan,
                    shipQuantityScheduled: matchedData.shipQuantityScheduled,
                  },
                  {
                    recDate: matchedData.recDate,
                    systemDate: rowCalcParams.systemDate,
                    todayShipRecDelayFlg: rowCalcParams.todayShipRecDelayFlg,
                  }
                ),
            enableUpdate: matchedData.enableUpdateShip || false,
            isUpdate: matchedData.isUpdatedShip || false,
            recType: day.rectype,
            enableData: matchedData.enableShip || false,
          }

          salesOrderRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.salesorderQuantity,
            enableUpdate: false,
            isUpdate: false,
            recType: day.rectype,
          }

          stockQuantityRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.stockQuantity,
            minStockQuantity: matchedData.minStockQuantity,
            enableUpdate: false,
            isUpdate: false,
            recType: day.rectype,
          }
          stockQuantityRecRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.stockQuantityRec,
            enableUpdate: false,
            isUpdate: false,
            recType: day.rectype,
          }

          calcSafetyRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.calcSafety,
            enableUpdate: false,
            isUpdate: false,
            recType: day.rectype,
          }

          stockQuantityShipStopRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.stockQuantityShipStop ?? null,
            enableUpdate: false,
            isUpdate: false,
            recType: day.rectype,
          }

          stockDaysRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.stockDays,
            stockDaysSetting: matchedData.stockDaysSetting,
            shipdaysNextArrival: matchedData.shipdaysNextArrival,
            enableUpdate: false,
            isUpdate: false,
            recType: day.rectype,
          }

          avgShipQuantityStockBaseRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.avgShipQuantityStockBase,
            enableUpdate: false,
            isUpdate: false,
            recType: day.rectype,
          }

          remarksRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.remarks ? matchedData.remarks : '',
            enableUpdate: true,
            isUpdate: !!matchedData.remarks,
            recType: day.rectype,
          }

          orderQuantityRecRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.orderQuantityRec,
            enableUpdate: false,
            isUpdate: false,
            recType: day.rectype,
          }

          orderQuantityRecommendRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.orderQuantityRecommend,
            minStockQuantity: matchedData.minStockQuantity,
            enableUpdate: false,
            isUpdate: false,
            recType: day.rectype,
          }

          arrivalQuantityPlanRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.arrivalQuantityPlan,
            enableUpdate: matchedData.enableUpdateArrival || false,
            isUpdate: matchedData.arrivalQuantityPlan != null,
            recType: day.rectype,
          }

          orderQuantityRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.orderQuantity,
            isUpdatedArrivalTmp: matchedData.isUpdatedArrivalTmp || false,
            isOrderConfirmed: matchedData.isOrderConfirmed || false,
            enableUpdate: false,
            isUpdate: false,
            recType: day.rectype,
          }

          shipQuantityExpectedRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.shipQuantityExpected,
            enableUpdate: false,
            isUpdate: false,
            recType: day.rectype,
          }

          shipQuantityPlanRow[`col${day.dateno}`] = {
            ...commonColumn,
            value: matchedData.shipQuantityPlan,
            enableUpdate: matchedData.enableUpdateShip || false,
            isUpdate: matchedData.shipQuantityPlan != null,
            recType: day.rectype,
          }
        } else {
          arrivalRow[`col${day.dateno}`] = null
          orderQuantityRecRow[`col${day.dateno}`] = null
          orderQuantityRecommendRow[`col${day.dateno}`] = null
          arrivalQuantityPlanRow[`col${day.dateno}`] = null
          orderQuantityRow[`col${day.dateno}`] = null
          shipRow[`col${day.dateno}`] = null
          shipQuantityExpectedRow[`col${day.dateno}`] = null
          salesOrderRow[`col${day.dateno}`] = null
          shipQuantityPlanRow[`col${day.dateno}`] = null
          stockQuantityRow[`col${day.dateno}`] = null
          stockQuantityRecRow[`col${day.dateno}`] = null
          calcSafetyRow[`col${day.dateno}`] = null
          stockQuantityShipStopRow[`col${day.dateno}`] = null
          stockDaysRow[`col${day.dateno}`] = null
          avgShipQuantityStockBaseRow[`col${day.dateno}`] = null
          remarksRow[`col${day.dateno}`] = null
        }
      })

      rows.push(
        arrivalRow, orderQuantityRecRow, orderQuantityRecommendRow, arrivalQuantityPlanRow, orderQuantityRow,
        shipRow, shipQuantityExpectedRow, salesOrderRow, shipQuantityPlanRow,
        stockQuantityRow, stockQuantityRecRow, calcSafetyRow, stockQuantityShipStopRow, stockDaysRow, avgShipQuantityStockBaseRow,
        remarksRow,
      )
    })

    return rows
  }, [gridUnitType, supplierMap, trCompanyId, loginUserInfo?.systemDate, centerSearch])

  /**
   * Update row data when stock data changes
   */
  useEffect(() => {
    if (stockData?.list && displayDaylist.length > 0) {
      const rows = resultToRows(stockData.list, displayDaylist)
      setRowData(rows)
    } else {
      setRowData([])
    }
  }, [stockData, displayDaylist, resultToRows])

  // 検索メッセージ (sync API result from fetchStockData to display success/error)
  useEffect(() => {
    setResultMessage(result)
  }, [result])

  useEffect(() => {
    if (uploadResultMessage) {
      setResultMessage(uploadResultMessage)
    }
  }, [uploadResultMessage])

  useEffect(() => {
    setResultMessage(gridMessage)
  }, [gridMessage])

  // Compare calculated stock quantity with API stock quantity (only when fetching API stock, not when editing/optimizing)
  useEffect(() => {
    if (isLoading) {
      setMismatchedStockItemCodes([])
      return
    }

    if (stockData?.list && stockData.list.length > 0 && centerSearch && displayDaylist.length > 0) {
      const mismatched = new Set<string>()

      const clonedList = stockData.list.map(item => ({ ...item }))

      handleStockCalculation(
        clonedList,
        clonedList,
        loginUserInfo,
        { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays: calculateStockDaysHook },
        calcParams,
        false,
        false,
        {
          // Only compare when fetching API stock to detect calculation mismatches
          // Do NOT compare when editing (handleRegister) or optimizing (handleOrderRecommend)
          compareStockQuantityWithAPI: {
            daylist: displayDaylist,
            startDateTypeCode: Number(searchParam.startDateTypeCode) !== 0 ? Number(searchParam.startDateTypeCode) : Number(defaultStartCode),
            endDateTypeCode: Number(searchParam.endDateTypeCode) !== 0 ? Number(searchParam.endDateTypeCode) : Number(defaultEndCode),
            mismatchedItemCodes: mismatched,
            // development: debug log. Production: false.
            logDetails: false,
          },
          centerSearch,
        }
      )

      setMismatchedStockItemCodes(Array.from(mismatched))
    } else {
      setMismatchedStockItemCodes([])
    }
  }, [isLoading, stockData?.list, displayDaylist, searchParam.startDateTypeCode, searchParam.endDateTypeCode, defaultStartCode, defaultEndCode, centerSearch, loginUserInfo, calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDaysHook])

  const onSearch = useCallback(async (requestParam: StockCenterSearchParam) => {
    setGridMenuResetSeq((n) => n + 1)
    setMismatchedStockItemCodes([])
    setRowData([])

    // orgItemCodes is only for UI selection stability (itemCode can be duplicated).
    // Do not send it to API.
    const apiParam = { ...(requestParam as any) }
    delete apiParam.orgItemCodes
    const centerSearchParam: GetMstCentersCenterGetRequest = {
      companyId: requestParam.companyId,
    }
    const promises: Promise<unknown>[] = [
      fetchStockData({ ...apiParam }),
      fetchCenterSearch(centerSearchParam),
      fetchSupplierSearch({ companyId: requestParam.companyId }, false),
      fetchAlertSummary({
        centerIds: requestParam.centerIds,
        orgSupplierIds: requestParam.orgSupplierIds,
        enabled: false,
      }),
    ]
    Promise.all(promises).catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error fetching data:', error)
    })

    setSearchParam(requestParam)

    if (
      loginUserInfo &&
      requestParam.companyId === loginUserInfo.companyId &&
      loginUserInfo?.tenantId &&
      loginUserInfo?.companyId
    ) {
      setSearchParamState<StockCenterSearchParam>(
        loginUserInfo?.tenantId,
        loginUserInfo?.companyId,
        'StockCenter',
        {
          ...requestParam,
        }
      )
    }

    setTrCompanyId(requestParam.companyId)

    navigate(
      `?company_id=${requestParam.companyId}&center_ids=${requestParam.centerIds?.join(',') || ''}&org_supplier_ids=${requestParam.orgSupplierIds?.join(',') || ''}&item_codes=${requestParam.itemCodes?.join(',') || ''}&view_unit_type=${requestParam.viewUnitType}&start_date=${requestParam.startDateTypeCode}&end_date=${requestParam.endDateTypeCode}`
    )

    setHasGridEdited(false)
  }, [loginUserInfo, fetchStockData, fetchCenterSearch, fetchSupplierSearch, fetchAlertSummary, setSearchParamState, navigate])

  const onReload = useCallback(() => {
    // Hard reset grid state first (discard local edits/marks),
    // then re-fetch to restore server values.
    const gridApi = gridSearch.gridRef.current?.api
    if (gridApi && !gridApi.isDestroyed()) {
      try {
        // Drop rowData to force ag-Grid to discard row-node state (including edited markers).
        gridApi.setGridOption('rowData', [])
      } catch {
        // ignore
      }
    }
    setRowData([])
    setHasGridEdited(false)
    setGridInstanceKey((k) => k + 1)
    onSearch(searchParam)
  }, [gridSearch.gridRef, onSearch, searchParam])

  const handleRenewalClick = useCallback((renewalFlg: boolean, centerId: string, itemCode: string) => {
    renewalSimulator.actions.openSimulator(renewalFlg, centerId, itemCode)
  }, [renewalSimulator.actions])

  const handleRenewalClose = useCallback(() => {
    renewalSimulator.actions.closeSimulator()
  }, [renewalSimulator.actions])

  const handleRenewalUpdate = useCallback(() => {
    renewalSimulator.actions.setUpdate(true)
  }, [renewalSimulator.actions])

  const handleRenewalUpdateComplete = useCallback(() => {
    handleRenewalClose()
    onReload()
  }, [handleRenewalClose, onReload])

  const handleItemInfoOpen = useCallback((centerId: number, itemCode: string) => {
    setItemInfoDialog({
      open: true,
      centerId,
      itemCode,
    })
  }, [])
  
  const handleItemInfoClose = useCallback(() => {
    setItemInfoDialog((prev) => ({ ...prev, open: false }))
  }, [])

  const handleOrderRecommend = useCallback(async () => {
    let responseStockOptimize: Stock[] = hasGridEdited ? [] : [...(stockData?.list ?? [])]
    const responseStockDayListCenterSupplier: DaylistCenterSupplier[] = hasGridEdited ? [] : [...(stockData?.daylistCenterSupplier ?? [])]
    const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())!
    systemDate.setHours(0, 0, 0, 0)
    setStockIsLoading(true)
    if (!stockData?.list || stockData?.list.length === 0) {
      setStockIsLoading(false)
      showResultMessage(setResultMessage, {
        status: 'warning',
        message: '対象データがありません。検索条件を変更して再度検索してください。'
      })
      return
    }

    // fetch center supplier data with orgSupplierId to get the correct data
    const responseCenterSupplier = await fetchCenterSupplierSearch({companyId: searchParam.companyId})
    const responseCenter = (await fetchCenterSearch({ companyId: searchParam.companyId })) as unknown

    const toPairKey = (centerId: number, orgSupplierId: string) => `${centerId}__${orgSupplierId}`

    // Build center-supplier map for lookup
    const centerSupplierDataList = responseCenterSupplier?.data?.list ?? []
    const centerSupplierDataMap = new Map<string, MstCenterSupplier>()
    centerSupplierDataList.forEach((cs) => {
      if (cs.companyId === searchParam.companyId && cs.centerId != null && cs.orgSupplierId) {
        centerSupplierDataMap.set(toPairKey(cs.centerId, cs.orgSupplierId), cs)
      }
    })

    // Build center map for lookup
    const centerList = ((responseCenter as any)?.data?.list ?? centerSearch?.list ?? []) as MstCenter[]
    const centerMap = new Map<number, MstCenter>()
    centerList.forEach((c: MstCenter) => {
      if (c.companyId === searchParam.companyId && c.centerId != null) {
        centerMap.set(c.centerId, c)
      }
    })

    // Derive center–supplier pairs from response daylistCenterSupplier (do not use centerIds × orgSupplierIds from searchParam)
    const daylistForPairs =
      responseStockDayListCenterSupplier.length > 0
        ? responseStockDayListCenterSupplier
        : (stockData?.daylistCenterSupplier ?? [])

    const seenPairKeys = new Set<string>()
    const centerSupplierPairs: { centerId: number; orgSupplierId: string }[] = []
    const pushPairIfValid = (centerId: number | null | undefined, orgSupplierId: string | null | undefined) => {
      if (centerId == null || Number.isNaN(centerId)) return
      if (!orgSupplierId) return
      const key = toPairKey(centerId, orgSupplierId)
      if (!centerSupplierDataMap.has(key) || seenPairKeys.has(key)) return
      seenPairKeys.add(key)
      centerSupplierPairs.push({ centerId, orgSupplierId })
    }

    daylistForPairs.forEach((d) => pushPairIfValid(d.centerId, d.orgSupplierId))

    if (centerSupplierPairs.length === 0) {
      setStockIsLoading(false)
      showResultMessage(setResultMessage, {
        status: 'warning',
        message: '最新のマスタ情報が取得できません'
      })
      return
    }

    // fix: 最適化期間の開始日(start_date)を画面表示中の在庫データから算出する。
    // start_date = 入荷日(is_arrival_day) かつ order_limit_date(=orderDate) >= operationDate を満たす最小の rec_date。
    // ds-model 側 build_optimization_period と同じ判定（API は order_limit_date を orderDate として返す）。
    const operationDate = systemDate
    const displayedStocks = stockData?.list ?? []
    const findOptimizeStartDate = (centerId: number, orgSupplierId: string): Date | null => {
      let minRec: Date | null = null
      displayedStocks.forEach((s) => {
        if (s.centerId !== centerId || s.orgSupplierId !== orgSupplierId) return
        if (s.isArrivalDay !== true) return
        const orderLimit = normalizeDate(s.orderDate)
        const rec = normalizeDate(s.recDate)
        if (!orderLimit || !rec) return
        if (orderLimit < operationDate) return
        if (minRec === null || rec < minRec) minRec = rec
      })
      return minRec
    }

    const pairOptions = centerSupplierPairs.map((pair) => {
      const cs = centerSupplierDataMap.get(toPairKey(pair.centerId, pair.orgSupplierId))!
      const optimizeRange = cs.optimizeRange ? Number(cs.optimizeRange) : Number(defaultOptimizeRange)
      const optimizeLeadtime = cs.trOrderLeadtime ? Number(cs.trOrderLeadtime) : 0
      // 取得・対象範囲の終端 = start_date + optimizeRange + 7日(安全マージン)。
      // start_date を算出できない場合は従来式へフォールバックし、従来範囲を下回らないよう大きい方を採用する。
      const startDate = findOptimizeStartDate(pair.centerId, pair.orgSupplierId)
      const fallbackEndDate = addDays(systemDate, optimizeRange + optimizeLeadtime + 7)
      const startBasedEndDate = startDate ? addDays(startDate, optimizeRange + 7) : fallbackEndDate
      const optimizeEndDate = startBasedEndDate > fallbackEndDate ? startBasedEndDate : fallbackEndDate
      const optimizeEndOffset = differenceInCalendarDays(optimizeEndDate, systemDate)
      return {
        ...pair,
        optimizeRange,
        optimizeLeadtime,
        optimizeStartDate: addDays(systemDate, -30),
        optimizeEndDate,
        optimizeEndOffset,
      }
    })


    try {
      const promises: Promise<any>[] = []
      if (hasGridEdited) {
        // Full fetch mode (used after "OK" in confirm dialog)
        promises.push(
          fetchStockData({
            ...searchParam,
            startDateTypeCode: String(-30),
            endDateTypeCode: String(-14),
          })
        )

        pairOptions.forEach((pair) => {
          promises.push(
            fetchStockData({
              ...searchParam,
              centerIds: [pair.centerId],
              orgSupplierIds: [pair.orgSupplierId],
              startDateTypeCode: String(0),
              endDateTypeCode: String(pair.optimizeEndOffset - 7),
            })
          )
        })
      } else {
        // Missing-only fetch mode (original behavior)
        if (Number(searchParam.startDateTypeCode) > -30) {
            promises.push(
              fetchStockData({
                ...searchParam,
                startDateTypeCode: String(-30),
                endDateTypeCode: String(Number(searchParam.startDateTypeCode) - 14)
              })
            )
        }
        // Missing-only fetch mode (original behavior)
        pairOptions.forEach((pair) => {
          if (Number(searchParam.endDateTypeCode) + 14 < pair.optimizeEndOffset) {
            promises.push(
              fetchStockData({
                ...searchParam,
                centerIds: [pair.centerId],
                orgSupplierIds: [pair.orgSupplierId],
                startDateTypeCode: String(Number(searchParam.endDateTypeCode ?? 0) + 14),
                endDateTypeCode: String(pair.optimizeEndOffset -7)
              })
            )
          }
        })
      }
      const resultsOptimize = await Promise.all(promises)
      if (resultsOptimize) {
        resultsOptimize.forEach((resultOptimize: { list: Stock[]; daylistCenterSupplier: DaylistCenterSupplier[] }) => {
          if (resultOptimize.list && resultOptimize.daylistCenterSupplier) {
            responseStockOptimize.push(...(resultOptimize.list ?? []))
            responseStockDayListCenterSupplier.push(...(resultOptimize.daylistCenterSupplier ?? []))
          }
        })
      }

      const daylistByPair = new Map<string, DaylistCenterSupplier[]>()
      responseStockDayListCenterSupplier.forEach((d) => {
        if (d.centerId != null && d.orgSupplierId) {
          const key = toPairKey(d.centerId, d.orgSupplierId)
          const arr = daylistByPair.get(key)
          if (arr) arr.push(d)
          else daylistByPair.set(key, [d])
        }
      })

      const pairWithUpdateDates = pairOptions.map((pair) => {
        const key = toPairKey(pair.centerId, pair.orgSupplierId)
        const updateDateSet = new Set<string>()
        ;(daylistByPair.get(key) ?? []).forEach((item) => {
          if (item.isOrderConfirmed !== false) return
          if (!item.orderDate) return
          if (item.orderDate < systemDate) return
          const d = normalizeDate(item.datevalue)
          if (!d) return
          const localDateKey = valueToDateStr(d)
          if (localDateKey) {
            updateDateSet.add(localDateKey)
          }
        })

        return { ...pair, updateDateSet }
      })

      setStockIsLoading(true)

      responseStockOptimize = handleStockCalculation(
        responseStockOptimize,
        responseStockOptimize ?? [],
        loginUserInfo,
        { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays: calculateStockDaysHook },
        calcParams,
        false,
        true,
        { centerSearch },
      )

      const stocksByPair = new Map<string, Stock[]>()
      responseStockOptimize.forEach((s) => {
        if (!s.companyId || s.companyId !== searchParam.companyId) return
        if (!s.centerId || !s.orgSupplierId) return
        if (!s.recDate) return
        const key = toPairKey(s.centerId, s.orgSupplierId)
        const arr = stocksByPair.get(key)
        if (arr) arr.push(s)
        else stocksByPair.set(key, [s])
      })

      const optimizeRequests = pairWithUpdateDates.map((pair) => {
        const key = toPairKey(pair.centerId, pair.orgSupplierId)
        const baseStocks = stocksByPair.get(key) ?? []

        const stockListForOptimize: OrderQuantityStockList[] = baseStocks
          .filter((stock) => stock.recDate && stock.recDate >= pair.optimizeStartDate && stock.recDate <= pair.optimizeEndDate)
          .map((stock) => ({
            companyId: stock.companyId!,
            centerId: stock.centerId!,
            orgSupplierId: stock.orgSupplierId!,
            itemCode: stock.itemCode!,
            shipQuantity: stock.shipQuantity ?? 0,
            shipQuantityOptimizeBase: stock.shipQuantityOptimizeBase ?? 0,
            stockQuantity: stock.stockQuantity ?? 0,
            arrivalQuantity: stock.arrivalQuantity ?? 0,
            arrivalFixFlg: stock.arrivalFixFlg ?? false,
            recDate: stock.recDate ? convertDateToRequestParam(normalizeDate(stock.recDate)!) : null,
            orderDate: stock.orderDate ? convertDateToRequestParam(normalizeDate(stock.orderDate)!) : null,
            safetyStockSetting: stock.safetyStockSetting ?? null,
            stockDaysSetting: stock.stockDaysSetting ?? null,
            minStockQuantity: stock.minStockQuantity ?? null,
            caseLot: stock.caseLot ?? 0,
            minOrderQuantity: stock.minOrderQuantity ?? 1,
            orderUnitType: stock.orderUnitType ?? '',
            weight: stock.weight ?? 0,
            arrivalRatio: stock.arrivalRatio ?? 0,
            stopArrival: stock.stopArrival ?? false,
            avgShipQuantityStockBase: stock.avgShipQuantityStockBase ?? 0,
            isArrivalDay: stock.isArrivalDay ?? false,
          }))

        if (stockListForOptimize.length === 0) {
          return Promise.resolve({
            ok: true,
            pairKey: key,
            orderRecommendList: [],
            updateDateSet: pair.updateDateSet ?? new Set<string>(),
          })
        }

        const cs = centerSupplierDataMap.get(key)
        const centerData = centerMap.get(pair.centerId)

        return optimizeOrderQuantity({
          operationDate: convertDateToRequestParam(normalizeDate(systemDate)!)!,
          shipPreviousStockFlg: Boolean(centerData?.shipPreviousStockFlg),
          centerSupplierInfo: {
            optimizeType: cs?.optimizeType ?? 'weight',
            trMinOrderQuantityOnce: cs?.trMinOrderQuantityOnce ?? 0,
            trOrderLeadtime: cs?.trOrderLeadtime ?? 0,
          },
          stockList: stockListForOptimize,
          optimizeRange: pair.optimizeRange ?? Number(defaultOptimizeRange),
        }).then((response) => ({
          ok: true,
          pairKey: key,
          ...response,
          updateDateSet: pair.updateDateSet ?? new Set<string>(),
        }))
      })

      const settled = await Promise.allSettled(optimizeRequests)
      const optimizeResults = settled
        .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled')
        .map((r) => r.value)

      const failedCount = settled.filter((r) => r.status === 'rejected').length
      const allOrderRecommendations = optimizeResults.flatMap((r) => r.orderRecommendList ?? [])

      if (allOrderRecommendations.length === 0) {
        setStockIsLoading(false)
        showResultMessage(setResultMessage, {
          status: 'warning',
          message: '発注推奨数算出対象データがありません。'
        })
        onReload()
        return
      }

      showResultMessage(setResultMessage, {
        status: failedCount > 0 ? 'warning' : 'success',
        message: failedCount > 0
          ? `発注数量最適化が完了しました。（一部失敗：${failedCount}件）`
          : '発注数量最適化が完了しました。'
      })

      let updatedStockList: Stock[] = []
      if (allOrderRecommendations.length > 0) {
        const recommendMap = new Map<string, any>()
        allOrderRecommendations.forEach((rec: any) => {
          const arrivalDate = normalizeDate(rec.arrivalDate)
          if (!rec.companyId || !rec.centerId || !rec.orgSupplierId || !rec.itemCode || !arrivalDate) return
          const arrivalDateKey = valueToDateStr(arrivalDate)
          if (!arrivalDateKey) return
          const key = `${rec.companyId}__${rec.centerId}__${rec.orgSupplierId}__${rec.itemCode}__${arrivalDateKey}`
          recommendMap.set(key, rec)
        })

        updatedStockList = responseStockOptimize.map((stock: Stock) => {
          const recDate = normalizeDate(stock.recDate)
          if (!stock.companyId || !stock.centerId || !stock.orgSupplierId || !stock.itemCode || !recDate) {
            return stock
          }
          const recDateKey = valueToDateStr(recDate)
          if (!recDateKey) {
            return stock
          }
          const key = `${stock.companyId}__${stock.centerId}__${stock.orgSupplierId}__${stock.itemCode}__${recDateKey}`
          const recommendation = recommendMap.get(key)
          if (!recommendation) return stock
          return {
            ...stock,
            arrivalQuantity: recommendation.orderQuantityRecommend,
            orderQuantityRecommend: recommendation.orderQuantityRecommend,
            orderRecommendCalcDatetime: normalizeDate(recommendation.orderRecommendCalcDatetime),
            orderQuantityLpResult: recommendation.orderQuantityLpResult ?? null,
            minStockForecastBased: recommendation.minStockForecastBased ?? null,
            minStockShortageBased: recommendation.minStockShortageBased ?? null,
            calcSafety: recommendation.calcSafety ?? null,
            isOrderQuantityFixed: stock.arrivalFixFlg,
            orderQuantity: stock.isUpdatedArrivalTmp ? null : stock.orderQuantity,
          }
        })
      } else {
        updatedStockList = responseStockOptimize.map((stock: Stock) => ({
          ...stock,
          orderQuantityRecommend: null,
          orderRecommendCalcDatetime: null,
          orderQuantityLpResult: null,
          minStockForecastBased: null,
          minStockShortageBased: null,
          calcSafety: null,
        } as Stock))
      }

      const stockListPlanUpdate = handleStockCalculation(
        responseStockOptimize,
        updatedStockList ?? [],
        loginUserInfo,
        { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays: calculateStockDaysHook },
        calcParams,
        false,
        true,
        { centerSearch },
      )
      
      // Filter update targets per (centerId, orgSupplierId) by the computed update-date set
      const updateDateSetByPair = new Map<string, Set<string>>()
      pairWithUpdateDates.forEach((p) => {
        const key = toPairKey(p.centerId, p.orgSupplierId)
        updateDateSetByPair.set(key, p.updateDateSet ?? new Set<string>())
      })

      const mergedUpdateStockList = stockListPlanUpdate.filter((item) => {
        if (!item.centerId || !item.orgSupplierId) return false
        const key = toPairKey(item.centerId, item.orgSupplierId)
        const set = updateDateSetByPair.get(key)
        if (!set || set.size === 0) return false
        const d = normalizeDate(item.recDate)
        if (!d) return false
        const itemDateKey = valueToDateStr(d)
        return itemDateKey ? set.has(itemDateKey) : false
      })
      handleUpdateStockCenter(mergedUpdateStockList)

    } catch (error: any) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: error.message || '発注推奨数算出処理中にエラーが発生しました。'
      })
      setStockIsLoading(false)
    }
  }, [stockData?.list, searchParam, centerSupplierSearchResult, centerSupplierSearch, centerSearch, optimizeOrderQuantity, loginUserInfo, fetchStockData, calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDaysHook, fetchCenterSearch])

  const handleUpdateStockCenter = useCallback(async(stockDataList: Stock[]) => {
    setStockIsLoading(true)

    if (!stockDataList || stockDataList.length === 0) {
      showResultMessage(setResultMessage, {
        status: 'warning',
        message: '更新対象の在庫データがありません。'
      })
      setStockIsLoading(false)
      onReload()
      return
    }

    const stockCenterList = stockDataList.map(item => ({
      companyId: item.companyId ?? 0,
      centerId: item.centerId ?? 0,
      itemCode: item.itemCode ?? '',
      orgSupplierId: item.orgSupplierId,
      recDate: item.recDate ? convertDateToRequestParam(normalizeDate(item.recDate)!) : null,
      stockQuantity: item.stockQuantity,
      stockDays: item.stockDays,
      orderQuantityRecommend: item.orderQuantityRecommend,
      orderRecommendCalcDatetime: item.orderRecommendCalcDatetime ? convertDateToRequestParam(normalizeDate(item.orderRecommendCalcDatetime)!) : null,
      orderQuantityLpResult: item.orderQuantityLpResult,
      minStockForecastBased: item.minStockForecastBased,
      minStockShortageBased: item.minStockShortageBased,
      calcSafety: item.calcSafety,
      shipQuantityStockBase: item.shipQuantityStockBase,
      avgShipQuantityStockBase: item.avgShipQuantityStockBase,
      isOrderQuantityFixed: item.isOrderQuantityFixed ?? null,
      stockQuantityRecMorning: item.stockQuantityRecMorning,
    }));

    const response = await fetchUpdateStockCenter({
      stockUpdate: {
        body: stockCenterList,
      }
    })
    showResultMessage(setResultMessage, {
      status: response.result.status as 'success' | 'warning' | 'error',
      message: response.result.message || '入出荷数の更新中にエラーが発生しました。'
    })
    onReload()
    setLoadingState(false)
  }, [fetchUpdateStockCenter])

  const onStockFileUpload = useCallback(async (file: File) => {
    setIsUploading(true)
    try {
      const response = await fetchStockUploadFile({ postedFile: file })
      if (response?.result.status === 'success') {
        fetchStockData({
          ...searchParam,
        })
      } else {
        setUploadResultMessage(response?.result)
      }
      setUploadResultMessage(response?.result)
    } catch (e) {
      setUploadResultMessage(e as ResponseResult)
    } finally {
      setIsUploading(false)
    }
  }, [fetchStockUploadFile, fetchStockData, searchParam])

  const allDayHeaders = useMemo<string[]>(() => {
    if (!displayDaylist.length) return []

    const headerList = displayDaylist
      .sort((a: Daylist, b: Daylist) => (a.dateno || 0) - (b.dateno || 0))
      .map((day) => day.colname)
    return headerList.filter((header): header is string => header !== null && header !== undefined)
  }, [displayDaylist])

  const firstColName = useCallback((): string => allDayHeaders[0] || 'col1', [allDayHeaders])

  const allNextCols = useCallback((col: string): string[] => {
    if (!allDayHeaders) return []
    const index = allDayHeaders.indexOf(col)
    if (index !== -1) {
      return allDayHeaders.slice(index)
    }
    return []
  }, [allDayHeaders])

  const beforeCol = useCallback((col: string) => {
    const index = parseInt(col.replace('col', ''), 10)
    return `col${index - 1}`
  }, [])

  const getTodayColName = useCallback((): string => {
    const todayCol = displayDaylist.find((day) => day.rectype === 'today')?.colname
    return todayCol && allDayHeaders.includes(todayCol) ? todayCol : firstColName()
  }, [displayDaylist, allDayHeaders, firstColName])

  const apiShipQuantityStockBaseMap = useMemo(() => {
    const map = new Map<string, number>()
    ;(stockData?.list ?? []).forEach((stock) => {
      if (
        stock.centerId == null ||
        !stock.itemCode ||
        !stock.recDate ||
        stock.shipQuantityStockBase == null
      ) {
        return
      }
      const recDateKey = valueToDateStr(normalizeDate(stock.recDate))
      if (!recDateKey) {
        return
      }
      const key = `${stock.centerId}|${stock.orgSupplierId ?? ''}|${stock.itemCode}|${recDateKey}`
      map.set(key, Number(stock.shipQuantityStockBase))
    })
    return map
  }, [stockData?.list])

  /**
   * Get a specific row type within the same group
   */
  const getRowInGroup = useCallback((rowNode: IRowNode<StockRow>, rowTypeCode: string): IRowNode<StockRow> | undefined => {
    const groupKey =
      rowNode.data?.groupKey ??
      rowNode.allLeafChildren?.find((n) => n?.data?.groupKey)?.data?.groupKey

    let groupNode: IRowNode<StockRow> | null = rowNode.parent
    if (groupNode?.parent?.allLeafChildren) {
      groupNode = groupNode.parent
    }
    let current: IRowNode<StockRow> | null = rowNode
    while (current) {
      if (current.group && current.rowGroupColumn?.getColId() === 'centerName') {
        groupNode = current
        break
      }
      current = current.parent
    }

    const allLeaves = groupNode?.allLeafChildren
    if (!allLeaves) {
      return undefined
    }
    return allLeaves.find((row) => {
      if (row?.data?.rowType?.code !== rowTypeCode || row.data?.isTotal) return false
      if (groupKey) return row.data?.groupKey === groupKey
      return true
    })
  }, [])

  /**
   * Leaf row nodes for the same product block as rowNode (same groupKey).
   * With multi-level row grouping, parent.childrenAfterGroup only lists siblings under one row-type bucket,
   * so onCellValueChanged must not rely on it alone — otherwise hasGridEdited never sets and 検索/初期化 skips the discard dialog.
   */
  const getRowNodesInProductGroup = useCallback((rowNode: IRowNode<StockRow>): IRowNode<StockRow>[] => {
    const groupKey =
      rowNode.data?.groupKey ??
      rowNode.allLeafChildren?.find((n) => n?.data?.groupKey)?.data?.groupKey

    let groupNode: IRowNode<StockRow> | null = rowNode.parent
    if (groupNode?.parent?.allLeafChildren) {
      groupNode = groupNode.parent
    }
    let current: IRowNode<StockRow> | null = rowNode
    while (current) {
      if (current.group && current.rowGroupColumn?.getColId() === 'centerName') {
        groupNode = current
        break
      }
      current = current.parent
    }

    const allLeaves = groupNode?.allLeafChildren
    if (!allLeaves?.length) {
      return []
    }
    if (groupKey == null || groupKey === '') {
      return allLeaves.filter((n) => n?.data && !n.data?.isTotal) as IRowNode<StockRow>[]
    }
    return allLeaves.filter(
      (n): n is IRowNode<StockRow> =>
        !!n?.data && !n.data.isTotal && n.data.groupKey === groupKey
    )
  }, [])

  /**
   * Calculate stock quantity for a specific cell
   * Business logic: stock = previous stock + arrival - ship - stockQuantityShipStop
   */
  const calcStock = useCallback((rowNode: IRowNode<StockRow>, field: string): StockCell => {
    // Get previous day's stock (negative values treated as 0)
    const beforeStockRow = getRowInGroup(rowNode, 'stock_quantity')
    const beforeCell = beforeStockRow?.data?.[beforeCol(field) as keyof StockRow] as StockCell
    const beforeStock = Math.max(beforeCell?.value || 0, 0)

    // Get today's arrival quantity
    const arrivalRow = getRowInGroup(rowNode, 'arrivalQuantity')
    const arrivalCell = arrivalRow?.data?.[field as keyof StockRow] as StockCell
    const arrival = arrivalCell?.value ?? 0

    // Get today's ship quantity
    const shipRow = getRowInGroup(rowNode, 'shipQuantity')
    const shipCell = shipRow?.data?.[field as keyof StockRow] as StockCell
    const ship = shipCell?.shipQuantityRaw ?? shipCell?.value ?? 0

    const salesOrderRow = getRowInGroup(rowNode, 'salesorderQuantity')
    const salesOrderCell = salesOrderRow?.data?.[field as keyof StockRow] as StockCell
    const salesOrder = salesOrderCell?.value ?? 0

    // Get current stock cell
    const stockRow = getRowInGroup(rowNode, 'stock_quantity')
    const stockCell = stockRow?.data?.[field as keyof StockRow] as StockCell

    const shipStopRow = getRowInGroup(rowNode, 'stockQuantityShipStop')
    const shipStopCell = shipStopRow?.data?.[field as keyof StockRow] as StockCell | undefined
    const shipStop = shipStopCell?.value ?? 0

    const rowCalcParams = buildCalcStockParamsForCenter(
      loginUserInfo?.systemDate,
      rowNode.data?.centerId ?? null,
      rowNode.data?.companyId ?? searchParam.companyId ?? null,
      centerSearch
    )

    const calculatedStock = gridCalc.calcCellStock(
      beforeStock,
      arrival,
      ship,
      salesOrder,
      shipCell?.recDate,
      rowCalcParams,
      shipStop
    )

    return {
      ...stockCell,
      value: calculatedStock,
    }
  }, [getRowInGroup, beforeCol, gridCalc, loginUserInfo?.systemDate, centerSearch, searchParam.companyId])

  const resolveShipQuantityForAvg = useCallback((
    rowNode: IRowNode<StockRow>,
    recDate: Date | string | null | undefined,
    shipQuantityRaw: number,
    rowCalcParams: CalcStockParams
  ): number => {
    const normalizedRecDate = normalizeDate(recDate)
    if (!normalizedRecDate) {
      return Number(shipQuantityRaw ?? 0)
    }

    // Past dates must keep API ship_quantity_stock_base (no client-side recalculation).
    if (normalizedRecDate < rowCalcParams.systemDate) {
      const recDateKey = valueToDateStr(normalizedRecDate)
      const apiKey = `${rowNode.data?.centerId ?? ''}|${rowNode.data?.orgSupplierId ?? ''}|${rowNode.data?.itemCode ?? ''}|${recDateKey ?? ''}`
      const apiShipQuantityStockBase = apiShipQuantityStockBaseMap.get(apiKey)
      return Number(apiShipQuantityStockBase ?? 0)
    }

    return Number(shipQuantityRaw ?? 0)
  }, [apiShipQuantityStockBaseMap])

  /**
   * Calculate stock days for a specific cell
   * Business logic: stock days = stock / average of next 7 business days' shipments
   */
  const calcStockDays = useCallback((rowNode: IRowNode<StockRow>, field: string): StockCell => {
    // Get current stock quantity
    const stockRow = getRowInGroup(rowNode, 'stock_quantity')
    const stockCell = stockRow?.data?.[field as keyof StockRow] as StockCell
    const stock = stockCell?.value ?? 0

    // Get ship and sales order rows in the same group
    const shipRow = getRowInGroup(rowNode, 'shipQuantity')
    const salesOrderRow = getRowInGroup(rowNode, 'salesorderQuantity')

    // Get current stock days cell
    const stockDaysRow = getRowInGroup(rowNode, 'stockDays')
    const stockDaysCell = stockDaysRow?.data?.[field as keyof StockRow] as StockCell

    const blankData = {
      ...stockDaysCell,
      value: null,
    }

    // Need at least 7 future business days for calculation
    const futureColsWithToday = allNextCols(field)
    if (futureColsWithToday.length <= 7) {
      return blankData
    }

    const rowCalcParams = buildCalcStockParamsForCenter(
      loginUserInfo?.systemDate,
      rowNode.data?.centerId ?? null,
      rowNode.data?.companyId ?? searchParam.companyId ?? null,
      centerSearch
    )

    const futureCols = futureColsWithToday.slice(1)
    const futureStocks = futureCols
      .map((col) => {
        const shipCell = shipRow?.data?.[col as keyof StockRow] as StockCell
        const salesOrderCell = salesOrderRow?.data?.[col as keyof StockRow] as StockCell
        const shipQuantityRaw = shipCell?.shipQuantityRaw ?? shipCell?.value ?? 0

        return {
          shipQuantity: resolveShipQuantityForAvg(rowNode, shipCell?.recDate, shipQuantityRaw, rowCalcParams),
          salesorderQuantity: salesOrderCell?.value ?? 0,
          enableShip: shipCell?.enableData ?? false,
          recDate: shipCell?.recDate,
        }
      })
      .filter(item => item.enableShip) // Only business days

    const calculatedStockDays = gridCalc.calcCellStockDays(
      stock,
      futureStocks,
      rowCalcParams
    )

    return {
      ...stockDaysCell,
      value: calculatedStockDays,
    }
  }, [getRowInGroup, allNextCols, gridCalc, loginUserInfo?.systemDate, centerSearch, searchParam.companyId, resolveShipQuantityForAvg])

  /**
   * 翌日～7営業日出荷数平均行を再計算。
   * onlyBeforeEditedDayField: 出荷planを1列だけ変えたとき、当該列より後ろの日の平均は「翌7営業日」に当該列が含まれないため再計算不要。
   */
  const updateAvgShipQuantityStockBaseForGroup = useCallback((
    rowNode: IRowNode<StockRow>,
    options?: { onlyBeforeEditedDayField?: string }
  ) => {
    const avgRow = getRowInGroup(rowNode, 'avgShipQuantityStockBase')
    if (!avgRow?.data) {
      return
    }

    const rawLimit = options?.onlyBeforeEditedDayField != null
      ? allDayHeaders.indexOf(options.onlyBeforeEditedDayField)
      : -1
    const limitColIdx = rawLimit >= 0 ? rawLimit : null

    const rowCalcParams = buildCalcStockParamsForCenter(
      loginUserInfo?.systemDate,
      rowNode.data?.centerId ?? null,
      rowNode.data?.companyId ?? searchParam.companyId ?? null,
      centerSearch
    )

    const shipRow = getRowInGroup(rowNode, 'shipQuantity')
    const salesOrderRow = getRowInGroup(rowNode, 'salesorderQuantity')

    allDayHeaders.forEach((col) => {
      if (limitColIdx != null) {
        const i = allDayHeaders.indexOf(col)
        if (i >= limitColIdx) {
          return
        }
      }

      const templateCell = avgRow.data?.[col as keyof StockRow] as StockCell | undefined
      if (!templateCell) {
        return
      }

      const futureColsWithToday = allNextCols(col)
      const futureCols = futureColsWithToday.slice(1)

      const futureStocks = futureCols.map((c) => {
        const shipCell = shipRow?.data?.[c as keyof StockRow] as StockCell
        const salesOrderCell = salesOrderRow?.data?.[c as keyof StockRow] as StockCell
        const shipQuantityRaw = shipCell?.shipQuantityRaw ?? shipCell?.value ?? 0
        return {
          shipQuantity: resolveShipQuantityForAvg(rowNode, shipCell?.recDate, shipQuantityRaw, rowCalcParams),
          salesorderQuantity: salesOrderCell?.value ?? 0,
          enableShip: shipCell?.enableData ?? false,
          recDate: shipCell?.recDate,
        }
      })

      const businessDays = futureStocks.filter((s) => s.enableShip).slice(0, 7)
      if (businessDays.length < 7) {
        avgRow.setDataValue(col, {
          ...templateCell,
          value: null,
        })
        return
      }

      const avgVal = calculateAvgShipQuantityStockBase(futureStocks, rowCalcParams)
      avgRow.setDataValue(col, {
        ...templateCell,
        value: avgVal,
      })
    })
  }, [getRowInGroup, allDayHeaders, allNextCols, calculateAvgShipQuantityStockBase, loginUserInfo?.systemDate, centerSearch, searchParam.companyId, resolveShipQuantityForAvg])

  /**
   * Calculate total for a specific row type across all date columns
   * @param rowTypeCodeSetter - The row type code for the total row (e.g., 'arrivalQuantity', 'shipQuantity')
   * @param rowTypeCodeGetter - The row type code for the data rows to sum (e.g., 'arrivalQuantity', 'shipQuantity')
   * @param allRowNodes - All row nodes in the grid
   * @param gridApi - The AG Grid API instance
   */
  const calcTotalByRowType = useCallback(
    (
      rowTypeCodeSetter: string,
      rowTypeCodeGetter: string,
      allRowNodes: IRowNode<StockRow>[],
      gridApi: any
    ) => {
      const totalRows = allRowNodes.filter(
        (node) => node.data?.isTotal && node.data?.rowType?.code === rowTypeCodeSetter
      )
      if (totalRows.length === 0) return

      const dataRowsByType = allRowNodes.filter(
        (node) => !node.data?.isTotal && node.data?.rowType?.code === rowTypeCodeGetter
      )

      totalRows.forEach((totalRow) => {
        const rows = filterLeafRowsByTotalScope(totalRow, dataRowsByType)
        allDayHeaders.forEach((col) => {
          const totalCol = totalRow.data?.[col as keyof StockRow] as StockCell
          if (!totalCol) return

          const total = rows.reduce((acc, row) => {
            const cell = row.data?.[col as keyof StockRow] as StockCell
            const cellValue = cell?.value || 0
            return Number(acc) + Number(cellValue < 0 ? 0 : cellValue)
          }, 0)

          const firstCell = rows?.[0]?.data?.[col as keyof StockRow] as StockCell

          totalRow.setDataValue(col, {
            ...totalCol,
            enableData: firstCell?.enableData || false,
            value: total,
          })
        })
      })

      gridApi.refreshCells({
        rowNodes: totalRows,
        force: true,
      })
    },
    [allDayHeaders]
  )

  /**
   * 合計行 1 セル分の avg_ship_quantity_stock_base.
   * shipTotal / businessDayCols は呼び出し側で 1 回だけ作って渡す（O(1) 参照）。
   * shipTotal.value は calcTotalByRowType('shipQuantity', ...) 適用後の Σship_quantity_stock_base。
   */
  const calcAvgShipForTotalCol = useCallback(
    (
      col: string,
      shipTotal: IRowNode<StockRow>,
      businessDayCols: ReadonlySet<string>
    ): number | null => {
      const futureCols = allNextCols(col).slice(1)
      if (futureCols.length < 7) return null

      const values: number[] = []
      for (let i = 0; i < futureCols.length && values.length < 7; i += 1) {
        const c = futureCols[i]
        if (businessDayCols.has(c)) {
          const cell = shipTotal.data?.[c as keyof StockRow] as StockCell | undefined
          values.push(Number(cell?.value ?? 0))
        }
      }
      if (values.length < 7) return null
      return values.reduce((acc, v) => acc + v, 0) / 7
    },
    [allNextCols]
  )

  /**
   * Calculate all total rows (センター合計)
   * This function calculates totals for arrival, ship, sales order, stock, and stock days
   */
  const calcTotal = useCallback(() => {
    const gridApi = gridSearch.gridRef.current?.api
    if (!gridApi) return

    const allRowNodes: IRowNode<StockRow>[] = []

    gridApi.forEachNode((node) => {
      allRowNodes.push(node)
    })

    // 1) sum 系の rowType（stock / ship / sales / arrival / plan）を先に確定
    calcTotalByRowType('arrivalQuantity', 'arrivalQuantity', allRowNodes, gridApi)
    calcTotalByRowType('arrivalQuantityPlan', 'arrivalQuantityPlan', allRowNodes, gridApi)
    calcTotalByRowType('shipQuantity', 'shipQuantity', allRowNodes, gridApi)
    calcTotalByRowType('shipQuantityPlan', 'shipQuantityPlan', allRowNodes, gridApi)
    calcTotalByRowType('salesorderQuantity', 'salesorderQuantity', allRowNodes, gridApi)
    calcTotalByRowType('stock_quantity', 'stock_quantity', allRowNodes, gridApi)

    // 合計行 lookup map: groupKey × rowTypeCode → IRowNode（O(1) 参照）
    const totalRowsByKey = new Map<string, Map<string, IRowNode<StockRow>>>()
    const allLeafShipRows: IRowNode<StockRow>[] = []
    allRowNodes.forEach((node) => {
      if (node.data?.isTotal) {
        const gk = node.data.groupKey ?? ''
        const code = node.data.rowType?.code ?? ''
        let inner = totalRowsByKey.get(gk)
        if (!inner) {
          inner = new Map()
          totalRowsByKey.set(gk, inner)
        }
        inner.set(code, node)
      } else if (node.data?.rowType?.code === 'shipQuantity') {
        allLeafShipRows.push(node)
      }
    })

    // 2) 合計の avg_ship_quantity_stock_base
    const totalAvgRows = allRowNodes.filter(
      (node) => node.data?.isTotal && node.data?.rowType?.code === 'avgShipQuantityStockBase'
    )
    totalAvgRows.forEach((avgRow) => {
      const groupKey = avgRow.data?.groupKey ?? ''
      const shipTotal = totalRowsByKey.get(groupKey)?.get('shipQuantity')
      if (!shipTotal) return

      // scope 内 leaf を OR して営業日 col を 1 度だけ算出
      const leafShipScope = filterLeafRowsByTotalScope(avgRow, allLeafShipRows)
      const businessDayCols = new Set<string>()
      leafShipScope.forEach((leaf) => {
        allDayHeaders.forEach((col) => {
          const cell = leaf.data?.[col as keyof StockRow] as StockCell | undefined
          if (cell?.enableData) businessDayCols.add(col)
        })
      })

      allDayHeaders.forEach((col) => {
        const templateCell = avgRow.data?.[col as keyof StockRow] as StockCell | undefined
        if (!templateCell) return
        const avg = calcAvgShipForTotalCol(col, shipTotal, businessDayCols)
        avgRow.setDataValue(col, { ...templateCell, value: avg })
      })
    })
    if (totalAvgRows.length > 0) {
      gridApi.refreshCells({ rowNodes: totalAvgRows, force: true })
    }

    // 3) 合計の stock_days = stock / avg（2) で確定した avgRow から直接読む）
    const totalStockDaysRows = allRowNodes.filter(
      (node) => node.data?.isTotal && node.data?.rowType?.code === 'stockDays'
    )
    totalStockDaysRows.forEach((stockDaysRow) => {
      const groupKey = stockDaysRow.data?.groupKey ?? ''
      const stockTotal = totalRowsByKey.get(groupKey)?.get('stock_quantity')
      const avgTotal = totalRowsByKey.get(groupKey)?.get('avgShipQuantityStockBase')

      allDayHeaders.forEach((col) => {
        const templateCell = stockDaysRow.data?.[col as keyof StockRow] as StockCell | undefined
        if (!templateCell) return
        const avg = Number((avgTotal?.data?.[col as keyof StockRow] as StockCell | undefined)?.value ?? 0)
        const stockQty = Number((stockTotal?.data?.[col as keyof StockRow] as StockCell | undefined)?.value ?? 0)
        const sd = avg > 0 ? stockQty / avg : null
        stockDaysRow.setDataValue(col, { ...templateCell, value: sd })
      })
    })
    if (totalStockDaysRows.length > 0) {
      gridApi.refreshCells({ rowNodes: totalStockDaysRows, force: true })
    }

  }, [gridSearch.gridRef, calcTotalByRowType, allDayHeaders, calcAvgShipForTotalCol])

  const calcTotalDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleCalcTotal = useCallback(() => {
    if (calcTotalDebounceRef.current != null) {
      clearTimeout(calcTotalDebounceRef.current)
    }
    calcTotalDebounceRef.current = setTimeout(() => {
      calcTotalDebounceRef.current = null
      calcTotal()
    }, 48)
  }, [calcTotal])

  useEffect(() => () => {
    if (calcTotalDebounceRef.current != null) {
      clearTimeout(calcTotalDebounceRef.current)
    }
  }, [])

  const findStockRecordForField = useCallback((rowDataForField: StockRow | undefined, recDate?: Date | null) => {
    if (!rowDataForField?.centerId || !rowDataForField?.itemCode || !recDate) {
      return undefined
    }

    return stockData?.list?.find((stock: Stock) =>
      stock.recDate &&
      stock.centerId === rowDataForField.centerId &&
      stock.itemCode === rowDataForField.itemCode &&
      (stock.orgSupplierId ?? '') === (rowDataForField.orgSupplierId ?? '') &&
      isEqualDate(stock.recDate, recDate)
    )
  }, [stockData?.list])

  const syncPlanDrivenDisplayRows = useCallback((
    rowNode: IRowNode<StockRow>,
    field: string,
    stockDataRecord?: Stock
  ) => {
    const rowCalcParams = buildCalcStockParamsForCenter(
      loginUserInfo?.systemDate,
      rowNode.data?.centerId ?? null,
      rowNode.data?.companyId ?? searchParam.companyId ?? null,
      centerSearch
    )
    const arrivalRow = getRowInGroup(rowNode, 'arrivalQuantity')
    const arrivalPlanRow = getRowInGroup(rowNode, 'arrivalQuantityPlan')
    const orderQuantityRecRow = getRowInGroup(rowNode, 'orderQuantityRec')
    const orderQuantityRecommendRow = getRowInGroup(rowNode, 'orderQuantityRecommend')
    const shipRow = getRowInGroup(rowNode, 'shipQuantity')
    const shipPlanRow = getRowInGroup(rowNode, 'shipQuantityPlan')
    const salesOrderRow = getRowInGroup(rowNode, 'salesorderQuantity')

    const arrivalCell = arrivalRow?.data?.[field as keyof StockRow] as StockCell | undefined
    const arrivalPlanCell = arrivalPlanRow?.data?.[field as keyof StockRow] as StockCell | undefined
    const orderQuantityRow = getRowInGroup(rowNode, 'orderQuantity')
    const orderQuantityCell = orderQuantityRow?.data?.[field as keyof StockRow] as StockCell | undefined
    const orderQuantityRecCell = orderQuantityRecRow?.data?.[field as keyof StockRow] as StockCell | undefined
    const orderQuantityRecommendCell = orderQuantityRecommendRow?.data?.[field as keyof StockRow] as StockCell | undefined
    const shipCell = shipRow?.data?.[field as keyof StockRow] as StockCell | undefined
    const shipPlanCell = shipPlanRow?.data?.[field as keyof StockRow] as StockCell | undefined
    const salesOrderCell = salesOrderRow?.data?.[field as keyof StockRow] as StockCell | undefined
    // Important: when user presses Delete, plan cell value is null.
    // We must keep null (clear intent), not fallback to API plan by using ??.
    // Row data may store missing columns as null (not undefined); guard both so we never read null.value.
    const resolvedArrivalPlanValue =
      arrivalPlanCell != null ? arrivalPlanCell.value : stockDataRecord?.arrivalQuantityPlan
    const resolvedShipPlanValue =
      shipPlanCell != null ? shipPlanCell.value : stockDataRecord?.shipQuantityPlan

    if (arrivalRow && arrivalCell) {
      const resolvedOrderQuantityRec =
        (orderQuantityRecCell?.value as number | null | undefined) ?? stockDataRecord?.orderQuantityRec
      const resolvedOrderQuantityRecommend =
        (orderQuantityRecommendCell?.value as number | null | undefined) ??
        stockDataRecord?.orderQuantityRecommend
      const resolvedOrderQuantity =
        (orderQuantityCell?.value as number | null | undefined) ?? stockDataRecord?.orderQuantity
      const effectiveArrival = getStockCenter2EffectiveArrivalQuantity({
        enableUpdateArrival: stockDataRecord?.enableUpdateArrival,
        arrivalQuantity: stockDataRecord?.arrivalQuantity,
        orderQuantity: resolvedOrderQuantity,
        arrivalQuantityPlan: resolvedArrivalPlanValue,
        orderQuantityRec: resolvedOrderQuantityRec,
        orderQuantityRecommend: resolvedOrderQuantityRecommend,
      })

      arrivalRow.setDataValue(field, {
        ...arrivalCell,
        value: effectiveArrival,
        orderQuantityRec: resolvedOrderQuantityRec,
        orderQuantityRecommend: resolvedOrderQuantityRecommend,
        orderQuantity: resolvedOrderQuantity,
        isUpdate: arrivalPlanCell?.isUpdate ?? arrivalCell.isUpdate,
        isCurrentUpdate: arrivalPlanCell?.isCurrentUpdate ?? arrivalCell.isCurrentUpdate,
        resetToRecommend: arrivalPlanCell?.resetToRecommend ?? arrivalCell.resetToRecommend,
        oldValue: arrivalPlanCell?.oldValue ?? arrivalCell.oldValue,
      })
    }

    if (shipRow && shipCell) {
      const shipQuantityRaw = getStockCenter2ShipRawValue(
        {
          shipQuantityPlan: resolvedShipPlanValue,
          shipQuantityScheduled: stockDataRecord?.shipQuantityScheduled,
        },
        {
          recDate: shipCell.recDate ?? stockDataRecord?.recDate,
          systemDate: rowCalcParams.systemDate,
          todayShipRecDelayFlg: rowCalcParams.todayShipRecDelayFlg,
        }
      )

      shipRow.setDataValue(field, {
        ...shipCell,
        value: calculateStockCenter2ShipDisplayValue({
          shipQuantityPlan: resolvedShipPlanValue,
          salesorderQuantity: salesOrderCell?.value ?? stockDataRecord?.salesorderQuantity,
          recDate: shipCell.recDate ?? stockDataRecord?.recDate,
          shipQuantityScheduled: stockDataRecord?.shipQuantityScheduled,
        }, rowCalcParams),
        shipQuantityRaw,
        isUpdate: shipPlanCell?.isUpdate ?? shipCell.isUpdate,
        isCurrentUpdate: shipPlanCell?.isCurrentUpdate ?? shipCell.isCurrentUpdate,
        resetToRecommend: shipPlanCell?.resetToRecommend ?? shipCell.resetToRecommend,
        oldValue: shipPlanCell?.oldValue ?? shipCell.oldValue,
      })
    }
  }, [getRowInGroup, loginUserInfo?.systemDate, centerSearch, searchParam.companyId])

  const recalculateGroupStockMetrics = useCallback((
    rowNode: IRowNode<StockRow>,
    field: string,
    stockRec?: Stock,
    editedPlanRowType?: string
  ) => {
    const todayCol = getTodayColName()
    const fieldIdx = allDayHeaders.indexOf(field)
    const todayIdx = allDayHeaders.indexOf(todayCol)
    const startCol =
      fieldIdx >= 0 && todayIdx >= 0 && fieldIdx < todayIdx
        ? todayCol
        : field

    syncPlanDrivenDisplayRows(rowNode, startCol, stockRec)
    allNextCols(startCol).forEach((col) => {
      const stockRow = getRowInGroup(rowNode, 'stock_quantity')
      if (stockRow) {
        stockRow.setDataValue(col, calcStock(rowNode, col))
      }
    })
    allDayHeaders.forEach((col) => {
      const stockDaysRow = getRowInGroup(rowNode, 'stockDays')
      if (stockDaysRow) {
        stockDaysRow.setDataValue(col, calcStockDays(rowNode, col))
      }
    })
    if (editedPlanRowType === 'shipQuantityPlan') {
      updateAvgShipQuantityStockBaseForGroup(rowNode, { onlyBeforeEditedDayField: field })
    } else if (editedPlanRowType !== 'arrivalQuantityPlan') {
      updateAvgShipQuantityStockBaseForGroup(rowNode)
    }

    // ship cell class depends on previous stock cell; refresh dependent cells explicitly.
    const gridApi = gridSearch.gridRef.current?.api
    if (gridApi && !gridApi.isDestroyed()) {
      const refreshCols = allNextCols(startCol)
      if (refreshCols.length > 0) {
        gridApi.refreshCells({
          columns: refreshCols,
          force: true,
        })
      } else {
        gridApi.refreshCells({ force: true })
      }
    }
  }, [
    syncPlanDrivenDisplayRows,
    getTodayColName,
    allDayHeaders,
    allNextCols,
    getRowInGroup,
    calcStock,
    calcStockDays,
    updateAvgShipQuantityStockBaseForGroup,
    gridSearch.gridRef,
  ])

  const onModelUpdated = useCallback((event: ModelUpdatedEvent<StockRow>) => {
    const gridApi = gridSearch.gridRef.current?.api
    if (!gridApi) return

    // Full grid recalc only on new rowData; skip expand/find/filter model updates to avoid React update storms.
    if (event.newData !== true) {
      return
    }

    // Find today's column
    const todayCol = getTodayColName()

    // Recalculate ship display/base values for each day in group
    gridApi.forEachNode((node) => {
      if (node.data && node.data.rowType?.code === 'stock_quantity' && !node.data.isTotal) {
        allNextCols(todayCol).forEach((col) => {
          const shipRow = getRowInGroup(node, 'shipQuantity')
          const shipCell = shipRow?.data?.[col as keyof StockRow] as StockCell | undefined
          const recDate = shipCell?.recDate ?? null
          const stockRec = findStockRecordForField(node.data, recDate)
          syncPlanDrivenDisplayRows(node, col, stockRec)
        })
      }
    })

    gridApi.forEachNode((node) => {
      if (node.data && node.data.rowType?.code === 'stock_quantity' && !node.data.isTotal) {
        allNextCols(todayCol).forEach((col) => {
          const stockValue = calcStock(node, col)
          node.setDataValue(col, stockValue)
        })
      }
    })

    gridApi.forEachNode((node) => {
      if (node.data && node.data.rowType?.code === 'stockDays' && !node.data.isTotal) {
        allNextCols(firstColName()).forEach((col) => {
          const stockDaysValue = calcStockDays(node, col)
          node.setDataValue(col, stockDaysValue)
        })
      }
    })

    gridApi.forEachNode((node) => {
      if (node.data?.rowType?.code === 'stock_quantity' && !node.data.isTotal) {
        updateAvgShipQuantityStockBaseForGroup(node)
      }
    })

    calcTotal()

  }, [gridSearch.gridRef, getTodayColName, allNextCols, getRowInGroup, findStockRecordForField, syncPlanDrivenDisplayRows, calcStock, calcStockDays, firstColName, calcTotal, updateAvgShipQuantityStockBaseForGroup])

  /**
   * Cell value changed handler
   * Updates the row data when a cell is edited and triggers stock/stock days recalculation
   */
  const onCellValueChanged = useCallback(
    (params: CellValueChangedEvent<StockRow>) => {
      if (suppressRegisterCellChangeRef.current) {
        return
      }

      const rowType = params.data?.rowType?.code
      const { field } = params.colDef

      if (!['remarks', 'arrivalQuantityPlan', 'shipQuantityPlan'].includes(rowType ?? '') || params.data?.isTotal) {
        return
      }

      if (!field) {
        return
      }

      const gridApi = gridSearch.gridRef.current?.api
      if (!gridApi) {
        return
      }

      if (!params.node.id) {
        return
      }

      let rowsInGroup = getRowNodesInProductGroup(params.node)
      if (rowsInGroup.length === 0) {
        const direct = params.node.parent?.childrenAfterGroup
        rowsInGroup = direct ? ([...direct] as IRowNode<StockRow>[]) : []
      }
      if (rowsInGroup.length === 0) {
        rowsInGroup = [params.node]
      }

      const thisRow = rowsInGroup.find((row: IRowNode<StockRow>) => row.id === params.node.id)
      const thisRowNode: IRowNode<StockRow> =
        (thisRow ? gridApi.getRowNode(thisRow.id || '') : null) ?? params.node

      const scalarNew = extractNewValueScalarFromCellEvent(params.newValue as unknown)
      const recDate = extractRecDateFromCellChange(params.newValue as unknown, params.oldValue)
      const stockDataRecord = findStockRecordForField(params.data, recDate)

      // StockCenter2 does not use fromDeleteKey tagging like StockCenter(legacy).
      // Keep delete flow on the same path as normal edits.

      const isPlanRow = rowType === 'arrivalQuantityPlan' || rowType === 'shipQuantityPlan'
      const planInputsEmpty =
        isPlanRow &&
        (scalarNew === '' ||
          scalarNew === null ||
          scalarNew === undefined)

      if (planInputsEmpty) {
        // setDataValue below will synchronously trigger cellValueChanged with newValue.value = null,
        // which will re-enter this same branch (where planInputsEmpty is still true).
        // If the current cell is already cleared, we must exit early to avoid recursive blocking of the UI thread (which could freeze the 合計 group).
        const currentCell = thisRowNode.data?.[field as keyof StockRow] as StockCell | undefined
        const alreadyCleared =
          currentCell != null &&
          currentCell.value === null &&
          currentCell.isUpdate === false &&
          currentCell.resetToRecommend === true &&
          currentCell.isCurrentUpdate === true
        if (alreadyCleared) {
          return
        }
        const overlay =
          params.newValue != null && typeof params.newValue === 'object' && !Array.isArray(params.newValue)
            ? (params.newValue as StockCell)
            : ({} as Partial<StockCell>)
        thisRowNode.setDataValue(field, {
          ...params.oldValue,
          ...overlay,
          value: null,
          isUpdate: false,
          resetToRecommend: true,
          isCurrentUpdate: true,
          oldValue: params.oldValue?.value ?? null,
          stockPlanUpdateDatetime: params.oldValue?.stockPlanUpdateDatetime,
        })
        recalculateGroupStockMetrics(params.node, field, stockDataRecord, rowType ?? undefined)
        setHasGridEdited(true)
        scheduleCalcTotal()
        return
      }

      const unchanged =
        rowType === 'remarks'
          ? String(params.oldValue?.value ?? '') === String(scalarNew ?? '')
          : isPlanRow && toValidNumber(params.oldValue?.value) === toValidNumber(scalarNew)

      // Handle case when value did not change (e.g. blur without edit)
      if (unchanged) {
        if (!params.newValue) {
          thisRowNode.setDataValue(field, { ...params.oldValue })
        }
        const stockRow = rowsInGroup.find((row: IRowNode<StockRow>) => row.data?.rowType?.code === 'stock_quantity')
        if (!stockRow || !stockRow.id) {
          return
        }

        const stockRowNode = gridApi.getRowNode(stockRow.id)
        if (!stockRowNode || !stockRowNode.data) {
          return
        }
        allNextCols(field).forEach((col) => {
          stockRowNode.setDataValue(col, calcStock(params.node, col))
        })

        const stockDaysRow = rowsInGroup.find((row: IRowNode<StockRow>) => row.data?.rowType?.code === 'stockDays')
        if (!stockDaysRow || !stockDaysRow.id) {
          return
        }
        allDayHeaders.forEach((col) => {
          stockDaysRow.setDataValue(col, calcStockDays(params.node, col))
        })
        if (rowType === 'shipQuantityPlan') {
          updateAvgShipQuantityStockBaseForGroup(params.node, { onlyBeforeEditedDayField: field })
        } else if (rowType !== 'arrivalQuantityPlan') {
          updateAvgShipQuantityStockBaseForGroup(params.node)
        }
        scheduleCalcTotal()
        return
      }

      const nvObj =
        params.newValue != null && typeof params.newValue === 'object' && !Array.isArray(params.newValue)
          ? (params.newValue as StockCell)
          : null

      if (nvObj && nvObj.value == null && !nvObj.resetToRecommend) {
        if (rowType === 'remarks') {
          thisRowNode.setDataValue(field, {
            ...params.oldValue,
            value: '',
            isCurrentUpdate: true,
            isUpdate: false,
            resetToRecommend: (scalarNew === '' || params.oldValue?.value === 0),
            oldValue: params.oldValue?.value ?? null,
            stockPlanUpdateDatetime: params.oldValue?.stockPlanUpdateDatetime,
          })
        } else if (!isPlanRow) {
          thisRowNode.setDataValue(field, {
            ...params.oldValue,
            value: 0,
            isCurrentUpdate: true,
            resetToRecommend: (scalarNew === '' || params.oldValue?.value === 0),
            oldValue: params.oldValue?.value ?? null,
            stockPlanUpdateDatetime: params.oldValue?.stockPlanUpdateDatetime,
          })
        }

        setHasGridEdited(true)
        return
      }

      if (['arrivalQuantityPlan', 'shipQuantityPlan'].includes(rowType ?? '') && !params.data.isTotal) {
        const planRowCalcParams = buildCalcStockParamsForCenter(
          loginUserInfo?.systemDate,
          params.data?.centerId ?? null,
          params.data?.companyId ?? searchParam.companyId ?? null,
          centerSearch
        )
        const baseValue = rowType === 'arrivalQuantityPlan'
          ? Number(stockDataRecord?.arrivalQuantity ?? 0)
          : getStockCenter2ShipRawValue(
            { shipQuantityPlan: null, shipQuantityScheduled: stockDataRecord?.shipQuantityScheduled },
            stockDataRecord?.recDate
              ? {
                  recDate: stockDataRecord.recDate,
                  systemDate: planRowCalcParams.systemDate,
                  todayShipRecDelayFlg: planRowCalcParams.todayShipRecDelayFlg,
                }
              : null
          )

        const normalizedPlanValue =
          scalarNew === '' || scalarNew === undefined ? null : toValidNumber(scalarNew)
        const isUpdateValue = isStockCenter2PlanOverride(normalizedPlanValue, baseValue)

        const planOverlay =
          params.newValue != null && typeof params.newValue === 'object' && !Array.isArray(params.newValue)
            ? (params.newValue as StockCell)
            : ({} as Partial<StockCell>)

        thisRowNode.setDataValue(field, {
          ...params.oldValue,
          ...planOverlay,
          value: normalizedPlanValue,
          isUpdate: isUpdateValue,
          resetToRecommend: !isUpdateValue,
          isCurrentUpdate: true,
          oldValue: params.oldValue?.value ?? null,
        })

        recalculateGroupStockMetrics(params.node, field, stockDataRecord, rowType ?? undefined)

        setHasGridEdited(true)
        scheduleCalcTotal()
        return
      }

      // Handle remarks row edits (newValue may be string from agLargeTextCellEditor or StockCell from valueParser)
      if (rowType === 'remarks' && !params.data?.isTotal) {
        const newRemark = String(scalarNew ?? '')
        const oldRemark = String(params.oldValue?.value ?? '')
        const hasRemarkChanged = newRemark !== oldRemark
        const updatedValue = {
          ...params.oldValue,
          value: newRemark,
          isUpdate: hasRemarkChanged && !!newRemark,
          isCurrentUpdate: true,
        }
        thisRowNode.setDataValue(field, updatedValue)
        setHasGridEdited(true)
        scheduleCalcTotal()
        return
      }

      setHasGridEdited(true)
      scheduleCalcTotal()
    },
    [
      allNextCols,
      allDayHeaders,
      getRowInGroup,
      calcStock,
      calcStockDays,
      scheduleCalcTotal,
      findStockRecordForField,
      recalculateGroupStockMetrics,
      updateAvgShipQuantityStockBaseForGroup,
      gridSearch.gridRef,
      getRowNodesInProductGroup,
      loginUserInfo?.systemDate,
      centerSearch,
      searchParam.companyId,
    ]
  )

  const getEditedCells = useCallback((): StockPlanUpdateBase[] => {
    const gridApi = gridSearch.gridRef.current?.api
    if (!gridApi) return []

    const rowNodes: IRowNode<StockRow>[] = []
    gridApi.forEachNode((node) => {
      if (node && ['salesorderQuantity', 'remarks', 'arrivalQuantityPlan', 'shipQuantityPlan'].includes(node.data?.rowType?.code || '')) {
        rowNodes.push(node)
      }
    })

    const dataList: StockPlanUpdateBase[] = []
    const allCols = allNextCols(firstColName())

    const allCells = rowNodes
      .filter((node) => !!node.data)
      .map((node) => allCols.map((col) => {
        const cellData = node.data![col as keyof StockRow] as StockCell

        return {
          ...cellData,
          companyId: node.data!.companyId,
          centerId: node.data!.centerId,
          itemCode: node.data!.itemCode,
          orgSupplierId: node.data!.orgSupplierId,
          rowTypeCode: node.data?.rowType?.code,
        }
      }))
      .flat()

    allCells.forEach((cellData) => {
      if (cellData && cellData.isCurrentUpdate) {
        const existedData = dataList.find((d) => {
          const isSameDate =
            d.recDate && cellData.recDate ? isSameDay(d.recDate, cellData.recDate) : false

          return (
            d.companyId === cellData.companyId &&
            d.centerId === cellData.centerId &&
            (d.orgSupplierId ?? '') === (cellData.orgSupplierId ?? '') &&
            d.itemCode === cellData.itemCode &&
            isSameDate
          )
        })

        if (existedData) {
          // Update existing record
          switch (cellData.rowTypeCode) {
            case 'arrivalQuantityPlan':
              existedData.arrivalQuantityPlan = (!cellData.isUpdate) ? null : cellData.value
              break
            case 'shipQuantityPlan':
              existedData.shipQuantityPlan = (!cellData.isUpdate) ? null : cellData.value
              break
            case 'remarks':
              existedData.remarks = (!cellData.isUpdate || !cellData.value) ? null : cellData.value.toString()
              break
            case 'salesorderQuantity':
              break
            default:
              break
          }
        } else {
          const newData: StockPlanUpdateBase = {
            companyId: cellData.companyId,
            centerId: cellData.centerId,
            itemCode: cellData.itemCode!,
            orgSupplierId: cellData.orgSupplierId ?? null,
            recDate: cellData.recDate,
            orderDate: cellData.orderDate,
            remarks: cellData.remarks,
            olditemShipStopFlg: cellData.olditemShipStopFlg,
            stockPlanUpdateDatetime: cellData.stockPlanUpdateDatetime ? convertDateToRequestParam(cellData.stockPlanUpdateDatetime) : null,
          }

          switch (cellData.rowTypeCode) {
            case 'arrivalQuantityPlan':
              dataList.push({
                ...newData,
                arrivalQuantityPlan: (!cellData.isUpdate) ? null : cellData.value,
              })
              break
            case 'shipQuantityPlan':
              dataList.push({
                ...newData,
                shipQuantityPlan: (!cellData.isUpdate) ? null : cellData.value,
              })
              break
            case 'remarks':
              dataList.push({
                ...newData,
                remarks: (!cellData.isUpdate || !cellData.value) ? null : cellData.value.toString(),
              })
              break
            default:
              break
          }
        }
      }
    })

    const findSameRecord = (sub: StockPlanUpdateBase, rowTypeCode: string, hasUpdateFlg: boolean) =>
      allCells.find((c) => {
        const isSameDate = c.recDate && sub.recDate ? isSameDay(c.recDate, sub.recDate) : false
        return (
          c.companyId === sub.companyId &&
          c.centerId === sub.centerId &&
          (c.orgSupplierId ?? '') === (sub.orgSupplierId ?? '') &&
          c.itemCode === sub.itemCode &&
          isSameDate &&
          c.rowTypeCode === rowTypeCode &&
          (hasUpdateFlg ? c.isUpdate : true)
        )
      })

    const requestDataList = dataList.map((dt) => {
      const d = { ...dt }

      if (d.arrivalQuantityPlan === undefined) {
        const updatedCell = findSameRecord(d, 'arrivalQuantityPlan', true)
        if (updatedCell) {
          d.arrivalQuantityPlan = updatedCell.value
        }
      }

      if (d.shipQuantityPlan === undefined) {
        const updatedCell = findSameRecord(d, 'shipQuantityPlan', true)
        if (updatedCell) {
          d.shipQuantityPlan = updatedCell.value
        }
      }

      if (d.remarks === undefined) {
        const updatedCell = findSameRecord(d, 'remarks', false)
        if (updatedCell && updatedCell.value) {
          d.remarks = updatedCell.value.toString()
        }
      }

      return d
    })

    return requestDataList.map((d) => ({
      ...d,
      recDate: d.recDate ? convertDateToRequestParam(d.recDate) : null,
      orderDate: d.orderDate ? convertDateToRequestParam(d.orderDate) : d.orderDate,
    }))
  }, [gridSearch.gridRef, allNextCols, firstColName])

  const handleRegister = useCallback(async (opts?: { skipReload?: boolean }) => {
    const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())!
    systemDate.setHours(0, 0, 0, 0)
    const datalist = getEditedCells()
    if (datalist.length === 0 && (mismatchedStockItemCodes ?? []).length === 0) {
      // Defensive fallback:
      // when UI is dirty (e.g. delete-cleared cells) but request payload is empty,
      // reload to normalize grid values/marks the same way as search/reset flows.
      if (hasGridEdited) {
        setHasGridEdited(false)
        onReload()
        return
      }
      setGridMessage({
        status: 'warning',
        message: '更新するデータがありません。',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return
    }
    const stockUpdateTargetKey = (centerId: number, orgSupplierId: string | null | undefined, itemCode: string) =>
      `${centerId}|${orgSupplierId ?? ''}|${itemCode}`

    const updateTargets = new Set<string>()
    datalist.forEach((e) => {
      if (e.companyId && e.centerId != null && e.itemCode) {
        updateTargets.add(stockUpdateTargetKey(e.centerId, e.orgSupplierId, e.itemCode))
      }
    })
    ;(mismatchedStockItemCodes ?? []).forEach((k) => updateTargets.add(k))

    const stockListRaw = stockData?.list ?? []
    const mergedList = stockListRaw.map((stock) => {
      const edit = datalist.find(
        (e) =>
          e.companyId === stock.companyId &&
          e.centerId === stock.centerId &&
          (e.orgSupplierId ?? '') === (stock.orgSupplierId ?? '') &&
          e.itemCode === stock.itemCode &&
          e.recDate &&
          stock.recDate &&
          isEqualDate(e.recDate, stock.recDate)
      )
      if (edit) {
        return {
          ...stock,
          arrivalQuantityPlan: edit.arrivalQuantityPlan ?? null,
          shipQuantityPlan: edit.shipQuantityPlan ?? null,
          remarks: edit.remarks ?? null
        }
      }
      return stock
    })
    const calculated = handleStockCalculation(
      stockListRaw,
      mergedList,
      loginUserInfo,
      {
        calculateShipQuantityStockBase,
        calculateStockQuantity,
        calculateAvgShipQuantityStockBase,
        calculateStockDays: calculateStockDaysHook,
      },
      calcParams,
      false,
      true,
      { centerSearch },
    )
    const stockListUpdate = calculated
      .filter((item) =>
        item.companyId &&
        item.centerId &&
        item.itemCode &&
        item.orgSupplierId &&
        item.recDate &&
        normalizeDate(item.recDate)! >= normalizeDate(systemDate)! &&
        updateTargets.has(stockUpdateTargetKey(item.centerId!, item.orgSupplierId, item.itemCode!))
      )
      .map((item) => ({
        companyId: item.companyId ?? 0,
        centerId: item.centerId ?? 0,
        itemCode: item.itemCode ?? '',
        orgSupplierId: item.orgSupplierId,
        recDate: item.recDate ? convertDateToRequestParam(normalizeDate(item.recDate)!) : null,
        stockQuantity: item.stockQuantity,
        stockDays: item.stockDays,
        orderQuantityRecommend: item.orderQuantityRecommend,
        orderRecommendCalcDatetime: item.orderRecommendCalcDatetime ? convertDateToRequestParam(normalizeDate(item.orderRecommendCalcDatetime)!) : null,
        orderQuantityLpResult: item.orderQuantityLpResult,
        minStockForecastBased: item.minStockForecastBased,
        minStockShortageBased: item.minStockShortageBased,
        calcSafety: item.calcSafety,
        shipQuantityStockBase: item.shipQuantityStockBase,
        avgShipQuantityStockBase: item.avgShipQuantityStockBase,
        stockQuantityRecMorning: item.stockQuantityRecMorning,
      }))

    try {
      await fetchUpdateStock({
        stockPlanUpdate: {
          body: datalist,
          stockList: stockListUpdate,
        },
      })

      // Clear "current updated" flags after successful save (setDataValue so ag-Grid / cellClass refresh reliably)
      const gridApi = gridSearch.gridRef.current?.api
      if (gridApi) {
        suppressRegisterCellChangeRef.current = true
        try {
          gridApi.forEachNode((node: IRowNode<StockRow>) => {
            if (!node.data) return
            const rt = node.data.rowType?.code
            if (!['remarks', 'arrivalQuantityPlan', 'shipQuantityPlan'].includes(rt ?? '')) return
            allDayHeaders.forEach((colKey) => {
              const cellData = node.data![colKey as keyof StockRow] as StockCell
              if (cellData && typeof cellData === 'object' && cellData.isCurrentUpdate) {
                node.setDataValue(colKey, { ...cellData, isCurrentUpdate: false })
              }
            })
          })
          gridApi.refreshCells({ force: true })
        } finally {
          window.setTimeout(() => {
            suppressRegisterCellChangeRef.current = false
          }, 0)
        }
      }

      setHasGridEdited(false)
      if (!opts?.skipReload) {
        onReload()
      }
    } catch (err: any) {
      if (err?.result) {
        setGridMessage(err.result)
      } else if (err?.error) {
        setGridMessage(err.error)
      } else {
        setGridMessage({
          status: 'error',
          message: err?.message || '更新中にエラーが発生しました。',
          systemDate: null,
          pageNo: 0,
          systemInfo: null,
        })
      }
    }
  }, [getEditedCells, fetchUpdateStock, gridSearch.gridRef, allDayHeaders, onReload, stockData?.list, loginUserInfo, centerSearch, calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDaysHook, mismatchedStockItemCodes, setMismatchedStockItemCodes, hasGridEdited])

  const handleFileDownload = (mode: string) => {
    const hasCenter = (searchParam.centerIds?.length ?? 0) > 0
    const hasSupplier = (searchParam.orgSupplierIds?.length ?? 0) > 0
    const hasItem = (searchParam.itemCodes?.length ?? 0) > 0
    if (!hasCenter && !hasSupplier && !hasItem) {
      return
    }
    const filename = '倉庫別発注商品一覧'
    const downloadParam: GetStockStockGetRequest = {
      companyId: searchParam.companyId,
      centerIds: searchParam.centerIds || undefined,
      orgSupplierIds: searchParam.orgSupplierIds || undefined,
      itemCodes: searchParam.itemCodes || undefined,
      startDate: searchParam.startDate || initSearchParam.startDate || undefined,
      endDate: searchParam.endDate || initSearchParam.endDate || undefined,
    }
    if (mode === 'csv') {
      fetchStockCsvDownload(filename, downloadParam)
    } else if (mode === 'xlsx') {
      fetchStockXlsxDownload(filename, downloadParam)
    }
  }

  useEffect(() => {
    const shouldFetchStock = initSearchParam.companyId &&
      ((initSearchParam.centerIds?.length ?? 0) > 0 ||
      (initSearchParam.orgSupplierIds?.length ?? 0) > 0 ||
      (initSearchParam.itemCodes?.length ?? 0) > 0)
    if (shouldFetchStock) {
      onSearch(initSearchParam)
    }
  }, [])

  return (
    <Box>
      <MasterSearchFrom
        result={resultMessage}
        onDoUpdate={handleRegister}
        onReload={onReload}
        onDownloadApiData={handleFileDownload}
        discardChangeDialogOpen={discardChangeDialogOpen}
        setDownloadMode={setDownloadMode}
        hasDataEdited={hasGridEdited}
        importRequiredFields={['']}
        headerJp={['']}
        lastUpdateDatetime={stockData?.lastUpdateDatetime}
        isImport={false}
        onFileUpload={onStockFileUpload}
        isUploading={isUploading}
        isUpload={loginUserInfo?.companyId === trCompanyId}
        onOrderRecommend={handleOrderRecommend}
        isOrderRecommend={loginUserInfo?.companyId === trCompanyId}
        apiDownload
        mismatchedStockItemCodes={mismatchedStockItemCodes}
        stockCenterOrderRecommend
        disableRegisterAndOrderRecommend={
          localItemCodes.length > 0 || (stockData?.list?.length ?? 0) === 0
        }
        disableReloadButton={disableToolbarActionsWithoutLocalSearch}
        disableDownloadButtons={disableToolbarActionsWithoutLocalSearch}
      >
        <SearchCondition
          searchParam={initSearchParam}
          onSearch={onSearch}
          setSupplierInfo={setSupplierInfo}
          setDiscardChangeDialogOpen={setDiscardChangeDialogOpen}
          searchTrigger={searchTrigger}
          hasGridEdited={hasGridEdited}
        />
      </MasterSearchFrom>

      <SearchBox
        gridRef={gridSearch.searchBoxProps.gridRef}
        findSearchValue={gridSearch.searchBoxProps.findSearchValue}
        setFindSearchValue={gridSearch.searchBoxProps.setFindSearchValue}
        activeMatchNum={gridSearch.searchBoxProps.activeMatchNum}
        onFindChanged={gridSearch.searchBoxProps.onFindChanged}
        isVisible={gridSearch.searchBoxProps.isVisible}
        onHide={gridSearch.searchBoxProps.onHide}
      />

      <DataList
        key={gridInstanceKey}
        data={stockData || { list: [], daylist: [], daylistCenterSupplier: [], datacount: 0, maxcount: 0, lastUpdateDatetime: null, optimizeRangeStartDate: null, optimizeRangeEndDate: null, renewalOrgItemList: [], transportLotList: [] }}
        rowData={rowData}
        unitType={gridUnitType}
        systemDate={loginUserInfo?.systemDate || undefined}
        startDateTypeCode={Number(searchParam.startDateTypeCode) !== 0 ? Number(searchParam.startDateTypeCode) : Number(defaultStartCode)}
        endDateTypeCode={Number(searchParam.endDateTypeCode) !== 0 ? Number(searchParam.endDateTypeCode) : Number(defaultEndCode)}
        isLoading={isLoading}
        isUpdating={isUpdating}
        setDownloadColumnDefs={setDownloadColDef}
        gridRef={gridSearch.gridRef}
        findSearchValue={gridSearch.gridSearchProps.findSearchValue}
        onFindChanged={gridSearch.gridSearchProps.onFindChanged}
        isSearchVisible={gridSearch.searchBoxProps.isVisible}
        onCellValueChanged={onCellValueChanged}
        onModelUpdated={onModelUpdated}
        tableEditable={loginUserInfo?.authType !== AuthType.ReadOnly}
        onInfoClick={handleItemInfoOpen}
        onRenewalClick={handleRenewalClick}
        gridMenuResetSeq={gridMenuResetSeq}
      />

      <ItemInfoDisplay
        open={itemInfoDialog.open}
        onClose={handleItemInfoClose}
        trCompanyId={trCompanyId}
        centerId={itemInfoDialog.centerId}
        itemCode={itemInfoDialog.itemCode}
      />

      <Modal
        keepMounted
        open={renewalSimulator.state.isOpen}
        onClose={handleRenewalClose}
        aria-labelledby="renewal-simulator-title"
        aria-describedby="renewal-simulator-description"
      >
        <DialogWrapper>
          {renewalSimulator.state.isOpen ? (
            <>
              <Typography component="div" variant="largeBold" id="renewal-simulator-title">
                商品リニューアルシミュレーター
              </Typography>
              <ItemRenewalSimulator
                trCompanyId={trCompanyId ?? loginUserInfo?.companyId}
                centerIdProps={renewalSimulator.state.centerId}
                itemCodeProps={renewalSimulator.state.itemCode}
                updateAccept={renewalSimulator.state.isUpdate}
                setUpdateAccept={renewalSimulator.actions.setUpdate}
                unitType={gridUnitType}
                renewalFlgProps={renewalSimulator.state.renewalFlg}
                onDialogOpen={renewalSimulator.state.isOpen}
                onDialogClose={handleRenewalUpdateComplete}
                onDialogCloseOnly={handleRenewalClose}
                setResultMessage={setGridMessage}
              />
            </>
          ) : (
            <div />
          )}
          <Grid direction="row" container justifyContent="end">
            {loginUserInfo?.authType !== AuthType.ReadOnly && loginUserInfo?.companyId === trCompanyId ? (
              <Grid component="div">
                <Button variant="contained" color="secondary" onClick={handleRenewalUpdate}>
                  OK
                </Button>
              </Grid>
            ) : (
              <div />
            )}
            <Grid component="div">
              <Button variant="contained" color="secondary" onClick={handleRenewalClose}>
                閉じる
              </Button>
            </Grid>
          </Grid>
        </DialogWrapper>
      </Modal>
    </Box>
  )
}

export default StockCenter2
